import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, ArrowLeft, Loader2, Sun, Moon, BarChart3, Info, BarChart2, X, Bot, CheckCircle2, XCircle, AlertCircle, Copy, Check, BookOpen, Settings } from 'lucide-react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../services/api';

interface SummaryRow {
  [key: string]: string | number;
}

interface DetailRow {
  [key: string]: any;
}

const METRIC_NAMES: Record<string, string> = {
  // Paying Members table
  'New active members': 'new_active_members.csv',
  'Returning members': 'returning_members.csv',
  'Quit members': 'quit_members.csv',
  'Temporary left': 'temporary_left.csv',
  'Paying members': 'paying_members.csv',
  // At-risk Members table
  '[0] At-risk': 'at_risk.csv',
  '[1] RenewExtend': 'renew_extend.csv',
  '[2] RenewCreate': 'renew_create.csv',
  '[4] Gap': 'gap.csv',
  '[5] UpsellCreate': 'upsell_create.csv',
};

export default function VisualizationPage() {
  const { versionId } = useParams<{ versionId: string }>();
  const navigate = useNavigate();

  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });

  const [summaryData, setSummaryData] = useState<{
    headers: string[];
    rows: SummaryRow[];
  } | null>(null);

  // Track which cell is expanded (instead of navigating to detail page)
  const [expandedCell, setExpandedCell] = useState<{
    metric: string;
    week: string;
    tableType: 'paying' | 'atRisk';
    data: {
      headers: string[];
      rows: DetailRow[];
    };
  } | null>(null);

  // Filter for detail table
  const [detailStudioFilter, setDetailStudioFilter] = useState<string>('');

  const [versionInfo, setVersionInfo] = useState<{
    dateRange: { startDate: string; endDate: string };
    timestamp: string;
    reportType?: 'weekly' | 'period' | 'monthly';
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPayingChart, setShowPayingChart] = useState(() => {
    const saved = localStorage.getItem('showPayingChart');
    return saved ? JSON.parse(saved) : false;
  });
  const [showAtRiskChart, setShowAtRiskChart] = useState(() => {
    const saved = localStorage.getItem('showAtRiskChart');
    return saved ? JSON.parse(saved) : false;
  });
  const [showGrowthRate, setShowGrowthRate] = useState(true); // Show Growth Rate by default

  // Chart visibility settings with localStorage
  const [chartVisibility, setChartVisibility] = useState(() => {
    const saved = localStorage.getItem('atRiskChartVisibility');
    return saved ? JSON.parse(saved) : { barChart: true, areaChart: true, pieChart: true, studioBreakdown: true, quitDaysChart: true };
  });
  const [payingChartVisibility, setPayingChartVisibility] = useState(() => {
    const saved = localStorage.getItem('payingChartVisibility');
    return saved ? JSON.parse(saved) : { stackedAreaChart: true, studioBreakdown: true };
  });
  const [showChartSettings, setShowChartSettings] = useState(false);
  const [showPayingChartSettings, setShowPayingChartSettings] = useState(false);

  // Studio breakdown state for At-risk Members
  const [studioBreakdownData, setStudioBreakdownData] = useState<{
    studioName: string;
    atRiskCount: number;
    quitCount: number;
    renewCount: number;
    gapCount: number;
  }[]>([]);

  // Quit days breakdown state - average days from last check-in to quit by studio
  const [quitDaysBreakdownData, setQuitDaysBreakdownData] = useState<{
    studioName: string;
    avgDaysFromLastCheckIn: number;
    minDays: number;
    maxDays: number;
    memberCount: number;
  }[]>([]);

  // Studio breakdown state for Paying Members
  const [payingStudioBreakdownData, setPayingStudioBreakdownData] = useState<{
    studioName: string;
    payingCount: number;
    newActiveCount: number;
    quitCount: number;
    returningCount: number;
    temporaryLeftCount: number;
  }[]>([]);
  const [showVerification, setShowVerification] = useState(false);
  const [showAtRiskVerification, setShowAtRiskVerification] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [tooltipModal, setTooltipModal] = useState<{ title: string; description: string; formula?: string; note?: string } | null>(null);
  const [verificationResults, setVerificationResults] = useState<{
    week: string;
    prevPaying: number;
    newActive: number;
    returning: number;
    quit: number;
    tempLeft: number;
    expectedPaying: number;
    actualPaying: number;
    isCorrect: boolean;
    difference: number;
  }[]>([]);
  const [atRiskVerificationResults, setAtRiskVerificationResults] = useState<{
    week: string;
    renewExtend: number;
    renewCreate: number;
    upsellCreate: number;
    renewTotal: number;
    quit: number;
    gap: number;
    expectedAtRisk: number;
    actualAtRisk: number;
    isCorrect: boolean;
    difference: number;
  }[]>([]);

  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    if (versionId) {
      loadSummary();
      loadVersionInfo();
    }
  }, [versionId]);

  // Close chart settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showChartSettings && !target.closest('.chart-settings-dropdown')) {
        setShowChartSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showChartSettings]);

  const loadVersionInfo = async () => {
    try {
      const response = await api.get(`/reports/versions/${versionId}`);
      setVersionInfo(response.data);
    } catch (err) {
      console.error('Error loading version info:', err);
    }
  };

  const loadSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/reports/versions/${versionId}/summary`);
      setSummaryData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (metric: string, week: string, tableType: 'paying' | 'atRisk') => {
    // Save scroll position before any state changes
    const scrollY = window.scrollY;

    // Helper to restore scroll position after render completes
    const restoreScroll = () => {
      // Use double requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: scrollY, behavior: 'instant' });
        });
      });
    };

    // Toggle: if clicking the same cell, collapse it
    if (expandedCell && expandedCell.metric === metric && expandedCell.week === week) {
      setExpandedCell(null);
      restoreScroll();
      return;
    }

    // Reset filter when loading new detail
    setDetailStudioFilter('');
    setLoading(true);
    setError(null);
    try {
      // Special case for calculated Renew Members - combine all renew files
      if (metric === '_renew_total') {
        const renewFiles = ['renew_extend.csv', 'renew_create.csv', 'upsell_create.csv'];
        const allRows: DetailRow[] = [];
        let combinedHeaders: string[] = [];

        for (const file of renewFiles) {
          try {
            const response = await api.get(
              `/reports/versions/${versionId}/detail/${file}?week=${encodeURIComponent(week)}`
            );

            // Fix: Derive headers from first row if empty
            let fileHeaders = response.data.headers || [];
            if (fileHeaders.length === 0 && response.data.rows && response.data.rows.length > 0) {
              fileHeaders = Object.keys(response.data.rows[0]);
            }

            if (fileHeaders.length > 0 && combinedHeaders.length === 0) {
              // Add a "Source" column to identify which file the data came from
              combinedHeaders = ['Source', ...fileHeaders];
            }
            // Add source info to each row
            const sourceName = file.replace('.csv', '').replace(/_/g, ' ');
            response.data.rows.forEach((row: DetailRow) => {
              allRows.push({ Source: sourceName, ...row });
            });
          } catch (err) {
            console.warn(`Could not load ${file}:`, err);
          }
        }

        setExpandedCell({
          metric: 'Renew Members (Combined)',
          week,
          tableType,
          data: {
            headers: combinedHeaders,
            rows: allRows,
          },
        });
      } else {
        const metricFile = METRIC_NAMES[metric];
        if (!metricFile) {
          setError('Metric không hợp lệ');
          return;
        }

        const response = await api.get(
          `/reports/versions/${versionId}/detail/${metricFile}?week=${encodeURIComponent(week)}`
        );

        // Fix: If headers is empty but rows exist, derive headers from first row
        let headers = response.data.headers || [];
        if (headers.length === 0 && response.data.rows && response.data.rows.length > 0) {
          headers = Object.keys(response.data.rows[0]);
        }

        setExpandedCell({
          metric,
          week,
          tableType,
          data: {
            headers,
            rows: response.data.rows,
          },
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Không thể tải dữ liệu chi tiết');
    } finally {
      setLoading(false);
      // Restore scroll position after state update to prevent page jump
      restoreScroll();
    }
  };

  // Removed unused exportCSV function - export is now handled inline in detail modal

  const theme = isDark ? {
    bg: 'bg-transparent',
    cardBg: 'bg-white/[0.03] backdrop-blur-2xl border-white/[0.08]',
    text: 'text-white',
    textMuted: 'text-white/60',
    textSecondary: 'text-white/80',
    border: 'border-white/[0.08]',
    borderLight: 'border-white/[0.05]',
    tableHeader: 'bg-white/[0.02] backdrop-blur-sm',
    tableRow: 'hover:bg-white/[0.02] transition-colors duration-200',
    metricRow: 'bg-gradient-to-r from-[#F59E0B]/10 to-[#FBBF24]/5 backdrop-blur-sm',
    buttonBg: 'bg-white/[0.05] hover:bg-white/[0.08] backdrop-blur-sm transition-colors duration-200',
    accent: 'text-[#F59E0B]',
    gradient: 'bg-gradient-to-br from-[#F59E0B] to-[#FBBF24]',
  } : {
    bg: 'bg-gray-50',
    cardBg: 'bg-white/80 backdrop-blur-xl',
    text: 'text-gray-900',
    textMuted: 'text-gray-500',
    textSecondary: 'text-gray-700',
    border: 'border-gray-200',
    borderLight: 'border-gray-100',
    tableHeader: 'bg-gray-50/80 backdrop-blur-sm',
    tableRow: 'hover:bg-gray-50 transition-colors duration-200',
    metricRow: 'bg-[#F59E0B]/10',
    buttonBg: 'bg-gray-100/80 hover:bg-gray-200/80 backdrop-blur-sm transition-colors duration-200',
    accent: 'text-[#F59E0B]',
    gradient: 'bg-gradient-to-br from-[#F59E0B] to-[#FBBF24]',
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('vi-VN');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={`min-h-screen ${theme.bg} transition-colors duration-300`}>
      {/* Header */}
      <header className={`${theme.cardBg} border-b ${theme.border} sticky top-0 z-20 ${isDark ? 'shadow-xl shadow-black/60' : ''}`}>
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className={`p-2 rounded-lg ${theme.buttonBg} ${theme.textMuted} transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B]`}
                title="Quay lại"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${theme.gradient} flex items-center justify-center shadow-lg shadow-[#F59E0B]/30`}>
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className={`text-2xl font-bold ${theme.text} tracking-tight`}>
                    {versionInfo?.reportType === 'monthly' ? 'Monthly Report Details' : versionInfo?.reportType === 'period' ? 'Period Report Details' : 'Weekly Report Details'}
                  </h1>
                  <p className={`text-sm font-medium mt-1 inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${isDark ? 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30' : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'}`}>
                    <span>📅</span>
                    {versionInfo
                      ? `${formatDate(versionInfo.dateRange.startDate)} → ${formatDate(versionInfo.dateRange.endDate)}`
                      : `Version: ${versionId}`
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => window.open('https://notebooklm.google.com/notebook/50d0f1f6-2c5e-4a37-b8ed-8e9b9e7ddc5f', '_blank')}
                className="px-4 py-2 rounded-lg bg-[#8B5CF6] text-white hover:bg-[#7C3AED] transition-all flex items-center gap-2 text-sm font-medium shadow-lg shadow-[#8B5CF6]/30 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]"
              >
                <BookOpen className="w-4 h-4" />
                Tutorial
              </button>
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg ${theme.buttonBg} ${theme.textMuted} transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B]`}
                title={isDark ? 'Light Mode' : 'Dark Mode'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1920px] mx-auto p-6">
        <div className={`${theme.cardBg} rounded-2xl border ${theme.border} overflow-hidden shadow-2xl ${isDark ? 'shadow-black/60' : 'shadow-black/20'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-10 h-10 text-[#F59E0B] animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-32">
              <p className={`${theme.textMuted} text-lg`}>{error}</p>
            </div>
          ) : summaryData ? (
            // Summary Tables - Split into 2 tables
            (() => {
              if (!summaryData.rows || summaryData.rows.length === 0) {
                return <div className={`text-center py-16 ${theme.textMuted}`}>Không có dữ liệu</div>;
              }

              const headers = summaryData.headers.length > 0
                ? summaryData.headers
                : Object.keys(summaryData.rows[0]);

              const weekKey = headers.find(h =>
                h.toLowerCase().trim() === 'week'
              ) || headers[0];

              // Check if this is a period or monthly report
              const isPeriodReport = versionInfo?.reportType === 'period';
              const isMonthlyReport = versionInfo?.reportType === 'monthly';
              const hasPeriodColumns = headers.includes('periodStart') && headers.includes('periodEnd');
              const hasMonthColumns = headers.includes('month') && headers.includes('monthStart') && headers.includes('monthEnd');

              // Get weeks (or period ranges for period reports, or months for monthly reports)
              const weeks: string[] = [];
              summaryData.rows.forEach((row) => {
                let week: string;
                if (isPeriodReport && hasPeriodColumns) {
                  // For period reports, combine periodStart and periodEnd into a range
                  const start = String(row['periodStart'] || '');
                  const end = String(row['periodEnd'] || '');
                  week = `${start}|${end}`; // Use | as separator
                } else if (isMonthlyReport && hasMonthColumns) {
                  // For monthly reports, use the month label
                  week = String(row['month'] || '');
                } else {
                  week = String(row[weekKey] || '');
                }
                if (week && !weeks.includes(week)) {
                  weeks.push(week);
                }
              });

              // Helper function to get ISO week number from a date
              const getISOWeek = (date: Date): number => {
                const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                const dayNum = d.getUTCDay() || 7;
                d.setUTCDate(d.getUTCDate() + 4 - dayNum);
                const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
                return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
              };

              // Parse week string and get ISO week number
              const getWeekNumber = (weekStr: string): string => {
                try {
                  // Check for YYYY-MM-DD format
                  if (/^\d{4}-\d{2}-\d{2}$/.test(weekStr)) {
                    const date = new Date(weekStr);
                    const isoWeek = getISOWeek(date);
                    return `Week ${isoWeek}`;
                  }

                  // Extract start date from format "W DD/MM/YYYY-DD/MM/YYYY"
                  const match = weekStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
                  if (match) {
                    const [, day, month, year] = match;
                    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    const isoWeek = getISOWeek(date);
                    return `Week ${isoWeek}`;
                  }
                  return '';
                } catch {
                  return '';
                }
              };

              // Get short month name from month label (e.g. "M 01/01/2025-31/01/2025" -> "Jan 25")
              const getMonthName = (monthStr: string): string => {
                try {
                  // Extract start date from format "M DD/MM/YYYY-DD/MM/YYYY"
                  const match = monthStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
                  if (match) {
                    const [, , month, year] = match;
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const monthIndex = parseInt(month) - 1;
                    const shortYear = year.slice(-2);
                    return `${monthNames[monthIndex]} ${shortYear}`;
                  }
                  return monthStr;
                } catch {
                  return monthStr;
                }
              };

              // Helper to get value for a metric and week/month
              const getValue = (metric: string, week: string): number => {
                let row;
                if (isPeriodReport && hasPeriodColumns && week.includes('|')) {
                  // For period reports, match by periodStart and periodEnd
                  const [start, end] = week.split('|');
                  row = summaryData.rows.find(r => String(r['periodStart']) === start && String(r['periodEnd']) === end);
                } else if (isMonthlyReport && hasMonthColumns) {
                  // For monthly reports, match by month column
                  row = summaryData.rows.find(r => String(r['month']) === week);
                } else {
                  row = summaryData.rows.find(r => String(r[weekKey]) === week);
                }
                if (!row) return 0;
                const val = row[metric];
                return typeof val === 'number' ? val : parseInt(String(val)) || 0;
              };

              // Metric type definition
              type MetricDef = {
                key: string;
                label: string;
                hasDetail: boolean;
                isHighlight?: boolean;
                isCalculated?: boolean;
                isPercentage?: boolean;
                tooltip?: {
                  title: string;
                  description: string;
                  formula?: string;
                  note?: string;
                };
              };

              // Define Table 1: Paying Members metrics
              const payingMetrics: MetricDef[] = [
                {
                  key: 'New active members',
                  label: 'New Active Members',
                  hasDetail: true,
                  tooltip: {
                    title: 'New Active Members',
                    description: 'Số lượng thành viên mới kích hoạt gói tập lần đầu trong tuần. Đây là những thành viên hoàn toàn mới, chưa từng có lịch sử tập trước đó.',
                    note: 'Click vào số liệu để xem danh sách chi tiết từng thành viên.'
                  }
                },
                {
                  key: 'Quit members',
                  label: 'Quit Members',
                  hasDetail: true,
                  tooltip: {
                    title: 'Quit Members',
                    description: 'Số lượng thành viên đã ngừng tập vĩnh viễn trong tuần. Gói tập của họ đã hết hạn và không được gia hạn hoặc đăng ký mới.',
                    note: 'Đây là chỉ số quan trọng để đánh giá tỷ lệ giữ chân khách hàng. Click để xem danh sách chi tiết.'
                  }
                },
                {
                  key: 'Temporary left',
                  label: 'Temporary Left',
                  hasDetail: true,
                  tooltip: {
                    title: 'Temporary Left (Freeze)',
                    description: 'Số lượng thành viên tạm dừng gói tập (freeze) trong tuần. Họ vẫn còn gói tập hợp lệ nhưng đang trong trạng thái tạm ngưng do yêu cầu cá nhân.',
                    note: 'Khác với Quit, những thành viên này có thể quay lại bất cứ lúc nào. Click để xem danh sách chi tiết.'
                  }
                },
                {
                  key: 'Returning members',
                  label: 'Returning Members',
                  hasDetail: true,
                  tooltip: {
                    title: 'Returning Members',
                    description: 'Số lượng thành viên quay lại sau khi đã ngừng tập. Đây là những người trước đây đã Quit nhưng nay đăng ký lại gói tập mới.',
                    note: 'Chỉ số này phản ánh hiệu quả của chiến lược win-back. Click để xem danh sách chi tiết.'
                  }
                },
                {
                  key: 'Paying members',
                  label: 'Paying Members',
                  hasDetail: true,
                  isHighlight: true,
                  tooltip: {
                    title: 'Paying Members (Total Active)',
                    description: 'Tổng số thành viên đang có gói tập còn hiệu lực tại thời điểm cuối tuần. Đây là chỉ số quan trọng nhất phản ánh quy mô khách hàng đang thanh toán.',
                    formula: 'Paying_N = Paying_{N-1} - Quit_N - TempLeft_N + New_N + Returning_N',
                    note: 'Chỉ số này được tính tự động dựa trên công thức. Click vào số liệu để xem danh sách chi tiết tất cả thành viên đang active.'
                  }
                },
                {
                  key: '_growth_rate',
                  label: 'Growth Rate (%)',
                  hasDetail: false,
                  isCalculated: true,
                  isPercentage: true,
                  tooltip: {
                    title: 'Growth Rate',
                    description: 'Tốc độ tăng trưởng của số lượng Paying Members so với tuần trước. Chỉ số này cho thấy tốc độ phát triển hoặc suy giảm của cơ sở khách hàng.',
                    formula: 'Growth Rate = ((New + Returning) - (Quit + TempLeft)) / Paying_{N-1} × 100%',
                    note: 'Growth Rate dương (>0%) cho thấy cơ sở khách hàng đang tăng trưởng. Growth Rate âm (<0%) cảnh báo cần cải thiện retention và acquisition.'
                  }
                },
              ];

              // Define Table 2: At-risk Members metrics (with calculated Renew)
              const atRiskMetrics: MetricDef[] = [
                {
                  key: '_renew_total',
                  label: 'Renew Members',
                  hasDetail: true,
                  isCalculated: true,
                  tooltip: {
                    title: 'Renew Members (Total)',
                    description: 'Tổng số thành viên đã thực hiện gia hạn gói tập trong tuần. Chỉ số này được tính tổng từ 3 loại gia hạn:',
                    formula: 'Renew = RenewExtend + RenewCreate + UpsellCreate',
                    note: '• RenewExtend: Gia hạn gói tập hiện tại\n• RenewCreate: Tạo gói tập mới sau khi gói cũ hết hạn\n• UpsellCreate: Nâng cấp lên gói tập cao cấp hơn\n\nChỉ số này phản ánh hiệu quả của chiến lược retention và upselling.'
                  }
                },
                {
                  key: 'Quit members',
                  label: 'Quit Members',
                  hasDetail: true,
                  tooltip: {
                    title: 'Quit Members',
                    description: 'Số lượng thành viên đã ngừng tập vĩnh viễn trong tuần do gói tập hết hạn và không gia hạn hoặc đăng ký mới.',
                    note: 'Chỉ số này cần được giảm thiểu thông qua các chiến lược retention và engagement. Click để xem danh sách chi tiết.'
                  }
                },
                {
                  key: '[4] Gap',
                  label: 'Gap Members',
                  hasDetail: true,
                  tooltip: {
                    title: 'Gap Members',
                    description: 'Số lượng thành viên có khoảng trống (gap) giữa các gói tập - tức là gói cũ đã hết hạn một khoảng thời gian rồi mới đăng ký gói mới.',
                    note: 'Gap members có nguy cơ cao không quay lại. Cần phân tích nguyên nhân và cải thiện chiến lược retention. Click để xem danh sách chi tiết.'
                  }
                },
                {
                  key: '[0] At-risk',
                  label: 'At-risk',
                  hasDetail: true,
                  isHighlight: true,
                  tooltip: {
                    title: 'At-risk Members',
                    description: 'Số lượng thành viên có nguy cơ cao ngừng tập - gói tập của họ sẽ hết hạn trong vòng 7 ngày tới nhưng chưa có động thái gia hạn.',
                    formula: 'At-risk ≈ (Renew + Quit + Gap) - Renewed',
                    note: 'Đây là chỉ số cảnh báo quan trọng nhất. Cần liên hệ ngay với những thành viên này để tư vấn gia hạn và giữ chân. Click để xem danh sách chi tiết và liên hệ.'
                  }
                },
              ];

              // Calculate Renew total for each week
              const getRenewTotal = (week: string): number => {
                return getValue('[1] RenewExtend', week) +
                  getValue('[2] RenewCreate', week) +
                  getValue('[5] UpsellCreate', week);
              };

              // Calculate Growth Rate for each week
              const getGrowthRate = (weekIndex: number): number => {
                if (weekIndex === 0) return 0; // No previous week for first week

                const currentWeek = weeks[weekIndex];
                const previousWeek = weeks[weekIndex - 1];

                const newMembers = getValue('New active members', currentWeek);
                const returning = getValue('Returning members', currentWeek);
                const quit = getValue('Quit members', currentWeek);
                const tempLeft = getValue('Temporary left', currentWeek);
                const previousPaying = getValue('Paying members', previousWeek);

                if (previousPaying === 0) return 0;

                const growthRate = ((newMembers + returning - quit - tempLeft) / previousPaying) * 100;
                return Math.round(growthRate * 100) / 100; // Round to 2 decimal places
              };

              // Chart colors for stacked bars
              const CHART_COLORS = {
                paying: {
                  'New Active Members': '#22c55e',      // green
                  'Quit Members': '#ef4444',            // red
                  'Temporary Left': '#f97316',          // orange
                  'Returning Members': '#3b82f6',       // blue
                  'Paying Members': '#06b6d4',          // cyan
                },
                atRisk: {
                  'Renew Members': '#22c55e',           // green
                  'Quit Members': '#ef4444',            // red
                  'Gap Members': '#f97316',             // orange
                  'At-risk': '#8b5cf6',                 // purple
                }
              };

              // Prepare chart data for a table
              const prepareChartData = (metrics: MetricDef[], tableType: 'paying' | 'atRisk') => {
                return weeks.map(week => {
                  const dataPoint: Record<string, string | number> = { week };
                  metrics.forEach(metric => {
                    const value = metric.isCalculated
                      ? getRenewTotal(week)
                      : getValue(metric.key, week);
                    dataPoint[metric.label] = value;
                  });
                  // Add Paying Members or At-risk for tooltip
                  if (tableType === 'paying') {
                    dataPoint['Paying Members'] = getValue('Paying members', week);
                  } else {
                    dataPoint['At-risk'] = getValue('[0] At-risk', week);
                  }
                  return dataPoint;
                });
              };

              // Prepare area chart data with percentages for At-risk
              const prepareAreaChartData = () => {
                return weeks.map(week => {
                  const renewExtend = getValue('[1] RenewExtend', week);
                  const renewCreate = getValue('[2] RenewCreate', week);
                  const upsellCreate = getValue('[5] UpsellCreate', week);
                  const renewTotal = renewExtend + renewCreate + upsellCreate;
                  const quit = getValue('Quit members', week);
                  const gap = getValue('[4] Gap', week);
                  const atRiskTotal = getValue('[0] At-risk', week);

                  // Calculate percentages
                  const renewPercent = atRiskTotal > 0 ? (renewTotal / atRiskTotal) * 100 : 0;
                  const quitPercent = atRiskTotal > 0 ? (quit / atRiskTotal) * 100 : 0;
                  const gapPercent = atRiskTotal > 0 ? (gap / atRiskTotal) * 100 : 0;

                  return {
                    week,
                    'Renew Members': Number(renewPercent.toFixed(1)),
                    'Quit Members': Number(quitPercent.toFixed(1)),
                    'Gap Members': Number(gapPercent.toFixed(1)),
                    // Keep absolute values for tooltip
                    _renewTotal: renewTotal,
                    _quit: quit,
                    _gap: gap,
                    _atRiskTotal: atRiskTotal,
                  };
                });
              };

              // Prepare pie chart data for the latest week
              const prepareLatestPieChartData = () => {
                if (weeks.length === 0) return [];
                const latestWeek = weeks[weeks.length - 1];
                const renewExtend = getValue('[1] RenewExtend', latestWeek);
                const renewCreate = getValue('[2] RenewCreate', latestWeek);
                const upsellCreate = getValue('[5] UpsellCreate', latestWeek);
                const renewTotal = renewExtend + renewCreate + upsellCreate;
                const quit = getValue('Quit members', latestWeek);
                const gap = getValue('[4] Gap', latestWeek);
                const atRiskTotal = getValue('[0] At-risk', latestWeek);

                const renewPercent = atRiskTotal > 0 ? (renewTotal / atRiskTotal) * 100 : 0;
                const quitPercent = atRiskTotal > 0 ? (quit / atRiskTotal) * 100 : 0;
                const gapPercent = atRiskTotal > 0 ? (gap / atRiskTotal) * 100 : 0;

                return [
                  { name: 'Renew Members', value: Number(renewPercent.toFixed(1)), count: renewTotal, color: '#22c55e' },
                  { name: 'Quit Members', value: Number(quitPercent.toFixed(1)), count: quit, color: '#ef4444' },
                  { name: 'Gap Members', value: Number(gapPercent.toFixed(1)), count: gap, color: '#f97316' },
                ];
              };

              // Prepare stacked area chart data for Paying Members
              const preparePayingStackedAreaData = () => {
                return weeks.map(week => {
                  const newActive = getValue('New active members', week);
                  const returning = getValue('Returning members', week);
                  const quit = getValue('Quit members', week);
                  const tempLeft = getValue('Temporary left', week);
                  const payingMembers = getValue('Paying members', week);

                  return {
                    week,
                    'New Active': newActive,
                    'Returning': returning,
                    'Quit': quit,
                    'Temporary Left': tempLeft,
                    'Paying Members': payingMembers,
                  };
                });
              };

              // Load and prepare studio breakdown data for at-risk members
              const loadStudioBreakdown = async () => {
                if (weeks.length === 0) return;
                const latestWeek = weeks[weeks.length - 1];

                try {
                  // Load at-risk members first
                  const atRiskResponse = await api.get(
                    `/reports/versions/${versionId}/detail/at_risk.csv?week=${encodeURIComponent(latestWeek)}`
                  );

                  // Get set of at-risk member IDs
                  const atRiskMemberIds = new Set<string>();
                  atRiskResponse.data.rows.forEach((row: any) => {
                    const memberId = row['Member Id'] || row['MemberId'];
                    if (memberId) {
                      atRiskMemberIds.add(memberId.trim());
                    }
                  });

                  // Load quit, renew, and gap data
                  const [quitResponse, renewExtendResponse, renewCreateResponse, upsellCreateResponse, gapResponse] = await Promise.all([
                    api.get(`/reports/versions/${versionId}/detail/quit_members.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                    api.get(`/reports/versions/${versionId}/detail/renew_extend.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                    api.get(`/reports/versions/${versionId}/detail/renew_create.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                    api.get(`/reports/versions/${versionId}/detail/upsell_create.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                    api.get(`/reports/versions/${versionId}/detail/gap.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                  ]);

                  console.log('At-risk count:', atRiskMemberIds.size);
                  console.log('Quit rows:', quitResponse.data.rows?.length || 0);
                  console.log('Renew rows:', (renewExtendResponse.data.rows?.length || 0) + (renewCreateResponse.data.rows?.length || 0) + (upsellCreateResponse.data.rows?.length || 0));
                  console.log('Gap rows:', gapResponse.data.rows?.length || 0);

                  // Count by studio
                  const studioStats: Record<string, { atRiskCount: number; quitCount: number; renewCount: number; gapCount: number }> = {};

                  // Count total at-risk members by studio
                  atRiskResponse.data.rows.forEach((row: any) => {
                    const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';
                    if (!studioStats[studioName]) {
                      studioStats[studioName] = { atRiskCount: 0, quitCount: 0, renewCount: 0, gapCount: 0 };
                    }
                    studioStats[studioName].atRiskCount += 1;
                  });

                  // Count quit members (only those who are at-risk) and collect quit days data
                  let quitMatched = 0;
                  const quitDaysStats: Record<string, { totalDays: number; count: number; minDays: number; maxDays: number }> = {};
                  quitResponse.data.rows?.forEach((row: any) => {
                    const memberId = (row['Member Id'] || row['MemberId'])?.trim();
                    const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';

                    // Count quit members that are at-risk
                    if (memberId && atRiskMemberIds.has(memberId)) {
                      if (!studioStats[studioName]) {
                        studioStats[studioName] = { atRiskCount: 0, quitCount: 0, renewCount: 0, gapCount: 0 };
                      }
                      studioStats[studioName].quitCount += 1;
                      quitMatched++;
                    }

                    // Collect days from last check-in to quit for ALL quit members (not just at-risk)
                    const daysFromLastCheckIn = row['Days From Last Check-In To Quit'];
                    if (daysFromLastCheckIn !== null && daysFromLastCheckIn !== undefined && daysFromLastCheckIn !== '') {
                      const days = Number(daysFromLastCheckIn);
                      if (!isNaN(days)) {
                        if (!quitDaysStats[studioName]) {
                          quitDaysStats[studioName] = { totalDays: 0, count: 0, minDays: days, maxDays: days };
                        }
                        quitDaysStats[studioName].totalDays += days;
                        quitDaysStats[studioName].count += 1;
                        quitDaysStats[studioName].minDays = Math.min(quitDaysStats[studioName].minDays, days);
                        quitDaysStats[studioName].maxDays = Math.max(quitDaysStats[studioName].maxDays, days);
                      }
                    }
                  });
                  console.log('Quit matched:', quitMatched);
                  console.log('Quit days stats:', quitDaysStats);

                  // Calculate average quit days by studio
                  const quitDaysData = Object.entries(quitDaysStats)
                    .map(([studioName, stats]) => ({
                      studioName,
                      avgDaysFromLastCheckIn: stats.count > 0 ? Math.round(stats.totalDays / stats.count) : 0,
                      minDays: stats.minDays,
                      maxDays: stats.maxDays,
                      memberCount: stats.count,
                    }))
                    .sort((a, b) => b.avgDaysFromLastCheckIn - a.avgDaysFromLastCheckIn);
                  console.log('Quit days data:', quitDaysData);
                  setQuitDaysBreakdownData(quitDaysData);

                  // Count renew members (only those who are at-risk)
                  let renewMatched = 0;
                  [renewExtendResponse, renewCreateResponse, upsellCreateResponse].forEach(response => {
                    response.data.rows?.forEach((row: any) => {
                      const memberId = (row['Member Id'] || row['MemberId'])?.trim();
                      if (memberId && atRiskMemberIds.has(memberId)) {
                        const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';
                        if (!studioStats[studioName]) {
                          studioStats[studioName] = { atRiskCount: 0, quitCount: 0, renewCount: 0, gapCount: 0 };
                        }
                        studioStats[studioName].renewCount += 1;
                        renewMatched++;
                      }
                    });
                  });
                  console.log('Renew matched:', renewMatched);

                  // Count gap members (only those who are at-risk)
                  let gapMatched = 0;
                  gapResponse.data.rows?.forEach((row: any) => {
                    const memberId = (row['Member Id'] || row['MemberId'])?.trim();
                    if (memberId && atRiskMemberIds.has(memberId)) {
                      const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';
                      if (!studioStats[studioName]) {
                        studioStats[studioName] = { atRiskCount: 0, quitCount: 0, renewCount: 0, gapCount: 0 };
                      }
                      studioStats[studioName].gapCount += 1;
                      gapMatched++;
                    }
                  });
                  console.log('Gap matched:', gapMatched);

                  // Convert to array and sort by at-risk count descending
                  const studioData = Object.entries(studioStats)
                    .map(([studioName, stats]) => ({
                      studioName,
                      atRiskCount: stats.atRiskCount,
                      quitCount: stats.quitCount,
                      renewCount: stats.renewCount,
                      gapCount: stats.gapCount,
                    }))
                    .sort((a, b) => b.atRiskCount - a.atRiskCount);

                  setStudioBreakdownData(studioData);
                } catch (err) {
                  console.error('Error loading studio breakdown:', err);
                }
              };

              // Load and prepare studio breakdown data for paying members
              const loadPayingStudioBreakdown = async () => {
                if (weeks.length === 0) return;
                const latestWeek = weeks[weeks.length - 1];

                try {
                  // Load all member data in parallel
                  const [payingResponse, newActiveResponse, quitResponse, returningResponse, tempLeftResponse] = await Promise.all([
                    api.get(`/reports/versions/${versionId}/detail/paying_members.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                    api.get(`/reports/versions/${versionId}/detail/new_active_members.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                    api.get(`/reports/versions/${versionId}/detail/quit_members.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                    api.get(`/reports/versions/${versionId}/detail/returning_members.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                    api.get(`/reports/versions/${versionId}/detail/temporary_left.csv?week=${encodeURIComponent(latestWeek)}`).catch(() => ({ data: { rows: [] } })),
                  ]);

                  // Count by studio
                  const studioStats: Record<string, {
                    payingCount: number;
                    newActiveCount: number;
                    quitCount: number;
                    returningCount: number;
                    temporaryLeftCount: number;
                  }> = {};

                  // Count paying members
                  payingResponse.data.rows?.forEach((row: any) => {
                    const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';
                    if (!studioStats[studioName]) {
                      studioStats[studioName] = { payingCount: 0, newActiveCount: 0, quitCount: 0, returningCount: 0, temporaryLeftCount: 0 };
                    }
                    studioStats[studioName].payingCount += 1;
                  });

                  // Count new active members
                  newActiveResponse.data.rows?.forEach((row: any) => {
                    const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';
                    if (!studioStats[studioName]) {
                      studioStats[studioName] = { payingCount: 0, newActiveCount: 0, quitCount: 0, returningCount: 0, temporaryLeftCount: 0 };
                    }
                    studioStats[studioName].newActiveCount += 1;
                  });

                  // Count quit members
                  quitResponse.data.rows?.forEach((row: any) => {
                    const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';
                    if (!studioStats[studioName]) {
                      studioStats[studioName] = { payingCount: 0, newActiveCount: 0, quitCount: 0, returningCount: 0, temporaryLeftCount: 0 };
                    }
                    studioStats[studioName].quitCount += 1;
                  });

                  // Count returning members
                  returningResponse.data.rows?.forEach((row: any) => {
                    const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';
                    if (!studioStats[studioName]) {
                      studioStats[studioName] = { payingCount: 0, newActiveCount: 0, quitCount: 0, returningCount: 0, temporaryLeftCount: 0 };
                    }
                    studioStats[studioName].returningCount += 1;
                  });

                  // Count temporary left members
                  tempLeftResponse.data.rows?.forEach((row: any) => {
                    const studioName = row['StudioName'] || row['Studio Name'] || 'Unknown';
                    if (!studioStats[studioName]) {
                      studioStats[studioName] = { payingCount: 0, newActiveCount: 0, quitCount: 0, returningCount: 0, temporaryLeftCount: 0 };
                    }
                    studioStats[studioName].temporaryLeftCount += 1;
                  });

                  // Convert to array and sort by paying count descending
                  const studioData = Object.entries(studioStats)
                    .map(([studioName, stats]) => ({
                      studioName,
                      payingCount: stats.payingCount,
                      newActiveCount: stats.newActiveCount,
                      quitCount: stats.quitCount,
                      returningCount: stats.returningCount,
                      temporaryLeftCount: stats.temporaryLeftCount,
                    }))
                    .sort((a, b) => b.payingCount - a.payingCount);

                  setPayingStudioBreakdownData(studioData);
                } catch (err) {
                  console.error('Error loading paying studio breakdown:', err);
                }
              };

              // Check if there are any discrepancies in Paying Members data
              const hasPayingDiscrepancies = () => {
                for (let i = 1; i < weeks.length; i++) {
                  const week = weeks[i];
                  const newActive = getValue('New active members', week);
                  const returning = getValue('Returning members', week);
                  const quit = getValue('Quit members', week);
                  const tempLeft = getValue('Temporary left', week);
                  const actualPaying = getValue('Paying members', week);
                  const prevPaying = getValue('Paying members', weeks[i - 1]);

                  const expectedPaying = prevPaying - quit - tempLeft + newActive + returning;
                  const difference = actualPaying - expectedPaying;

                  if (Math.abs(difference) >= 1) {
                    return true;
                  }
                }
                return false;
              };

              // Verify Paying Members formula: Paying_N = Paying_{N-1} - Quit_N - TempLeft_N + New_N + Returning_N
              const runVerification = () => {
                const results: typeof verificationResults = [];

                for (let i = 0; i < weeks.length; i++) {
                  const week = weeks[i];
                  const newActive = getValue('New active members', week);
                  const returning = getValue('Returning members', week);
                  const quit = getValue('Quit members', week);
                  const tempLeft = getValue('Temporary left', week);
                  const actualPaying = getValue('Paying members', week);

                  if (i === 0) {
                    // First week - no previous week to compare
                    results.push({
                      week,
                      prevPaying: 0,
                      newActive,
                      returning,
                      quit,
                      tempLeft,
                      expectedPaying: actualPaying, // First week is baseline
                      actualPaying,
                      isCorrect: true,
                      difference: 0,
                    });
                  } else {
                    const prevPaying = getValue('Paying members', weeks[i - 1]);
                    const expectedPaying = prevPaying - quit - tempLeft + newActive + returning;
                    const difference = actualPaying - expectedPaying;

                    results.push({
                      week,
                      prevPaying,
                      newActive,
                      returning,
                      quit,
                      tempLeft,
                      expectedPaying,
                      actualPaying,
                      isCorrect: Math.abs(difference) < 1, // Allow small rounding errors
                      difference,
                    });
                  }
                }

                setVerificationResults(results);
                setShowVerification(true);
              };

              // Render Verification Results
              const renderVerification = () => {
                if (!showVerification || verificationResults.length === 0) return null;

                const allCorrect = verificationResults.every(r => r.isCorrect);

                return (
                  <div className={`mt-4 p-4 rounded-xl ${isDark ? 'bg-white/[0.02] backdrop-blur-md border-white/[0.05]' : 'bg-gray-50/80 backdrop-blur-sm'} border ${isDark ? '' : theme.borderLight} ${isDark ? 'shadow-xl shadow-black/40' : 'shadow-lg'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5 text-[#F59E0B]" />
                        <h4 className={`text-base font-semibold ${theme.text}`}>Kết quả kiểm tra số liệu</h4>
                        {allCorrect ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-500 text-xs font-medium">
                            <CheckCircle2 className="w-3 h-3" />
                            Tất cả đúng
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 text-xs font-medium">
                            <AlertCircle className="w-3 h-3" />
                            Có sai lệch
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setShowVerification(false)}
                        className={`p-1.5 rounded-lg ${theme.buttonBg} ${theme.textMuted} hover:${theme.text} transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B]`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      {verificationResults.map((result, idx) => {
                        // Generate copy text for this result
                        const getCopyText = () => {
                          if (idx === 0) {
                            return `[${result.week}] Baseline: ${result.actualPaying.toLocaleString()} paying members`;
                          }
                          const formula = `${result.prevPaying} - ${result.quit} (quit) - ${result.tempLeft} (temp) + ${result.newActive} (new) + ${result.returning} (return) = ${result.expectedPaying}`;
                          const comparison = `Kỳ vọng: ${result.expectedPaying} | Thực tế: ${result.actualPaying}${!result.isCorrect ? ` (Chênh lệch: ${result.difference > 0 ? '+' : ''}${result.difference})` : ''}`;
                          return `[${result.week}] ${result.isCorrect ? '✓' : '✗'}\n${formula}\n${comparison}`;
                        };

                        const handleCopy = async () => {
                          try {
                            await navigator.clipboard.writeText(getCopyText());
                            setCopiedIndex(idx);
                            setTimeout(() => setCopiedIndex(null), 2000);
                          } catch (err) {
                            console.error('Failed to copy:', err);
                          }
                        };

                        return (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border ${result.isCorrect
                              ? isDark ? 'border-green-500/30 bg-green-500/5' : 'border-green-200 bg-green-50'
                              : isDark ? 'border-red-500/30 bg-red-500/5' : 'border-red-200 bg-red-50'
                              }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {result.isCorrect ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                )}
                                <span className={`font-medium ${theme.text}`}>{result.week}</span>
                              </div>
                              <button
                                onClick={handleCopy}
                                className={`p-1.5 rounded-lg transition-colors ${copiedIndex === idx
                                  ? 'bg-green-500/20 text-green-500'
                                  : 'bg-[#F59E0B] text-white hover:bg-[#DC7A02]'
                                  }`}
                                title="Copy log"
                              >
                                {copiedIndex === idx ? (
                                  <Check className="w-3.5 h-3.5" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>

                            {idx === 0 ? (
                              <p className={`text-sm ${theme.textMuted}`}>
                                📊 Tuần đầu tiên (baseline): <strong className={theme.text}>{result.actualPaying.toLocaleString()}</strong> paying members
                              </p>
                            ) : (
                              <div className={`text-sm ${theme.textMuted} space-y-1`}>
                                <p className="font-mono">
                                  <span className="text-blue-500">{result.prevPaying.toLocaleString()}</span>
                                  <span className="mx-1">−</span>
                                  <span className="text-red-500">{result.quit}</span>
                                  <span className={`text-xs ${theme.textMuted}`}> (quit)</span>
                                  <span className="mx-1">−</span>
                                  <span className="text-orange-500">{result.tempLeft}</span>
                                  <span className={`text-xs ${theme.textMuted}`}> (temp)</span>
                                  <span className="mx-1">+</span>
                                  <span className="text-green-500">{result.newActive}</span>
                                  <span className={`text-xs ${theme.textMuted}`}> (new)</span>
                                  <span className="mx-1">+</span>
                                  <span className="text-[#F59E0B]">{result.returning}</span>
                                  <span className={`text-xs ${theme.textMuted}`}> (return)</span>
                                  <span className="mx-1">=</span>
                                  <strong className={result.isCorrect ? 'text-green-500' : 'text-yellow-500'}>
                                    {result.expectedPaying.toLocaleString()}
                                  </strong>
                                </p>
                                <p>
                                  📊 Kỳ vọng: <strong>{result.expectedPaying.toLocaleString()}</strong>
                                  {' | '}
                                  Thực tế: <strong className={theme.text}>{result.actualPaying.toLocaleString()}</strong>
                                  {!result.isCorrect && (
                                    <span className="text-red-500 ml-2">
                                      (Chênh lệch: {result.difference > 0 ? '+' : ''}{result.difference})
                                    </span>
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    <div className={`mt-4 pt-3 border-t ${theme.borderLight} text-xs ${theme.textMuted}`}>
                      <p>
                        <strong>Công thức:</strong> Paying<sub>N</sub> = Paying<sub>N-1</sub> − Quit<sub>N</sub> − TempLeft<sub>N</sub> + New<sub>N</sub> + Returning<sub>N</sub>
                      </p>
                    </div>
                  </div>
                );
              };

              // Check if there are any discrepancies in At-risk data
              const hasAtRiskDiscrepancies = () => {
                for (const week of weeks) {
                  const renewExtend = getValue('[1] RenewExtend', week);
                  const renewCreate = getValue('[2] RenewCreate', week);
                  const upsellCreate = getValue('[5] UpsellCreate', week);
                  const renewTotal = renewExtend + renewCreate + upsellCreate;
                  const quit = getValue('Quit members', week);
                  const gap = getValue('[4] Gap', week);
                  const actualAtRisk = getValue('[0] At-risk', week);

                  const expectedAtRisk = renewTotal + quit + gap;
                  const difference = actualAtRisk - expectedAtRisk;

                  if (Math.abs(difference) >= 1) {
                    return true;
                  }
                }
                return false;
              };

              // Verify At-risk formula: At-risk = Renew + Quit + Gap
              const runAtRiskVerification = () => {
                const results: typeof atRiskVerificationResults = [];

                for (const week of weeks) {
                  const renewExtend = getValue('[1] RenewExtend', week);
                  const renewCreate = getValue('[2] RenewCreate', week);
                  const upsellCreate = getValue('[5] UpsellCreate', week);
                  const renewTotal = renewExtend + renewCreate + upsellCreate;
                  const quit = getValue('Quit members', week);
                  const gap = getValue('[4] Gap', week);
                  const actualAtRisk = getValue('[0] At-risk', week);

                  const expectedAtRisk = renewTotal + quit + gap;
                  const difference = actualAtRisk - expectedAtRisk;

                  results.push({
                    week,
                    renewExtend,
                    renewCreate,
                    upsellCreate,
                    renewTotal,
                    quit,
                    gap,
                    expectedAtRisk,
                    actualAtRisk,
                    isCorrect: Math.abs(difference) < 1,
                    difference,
                  });
                }

                setAtRiskVerificationResults(results);
                setShowAtRiskVerification(true);
              };

              // Render At-risk Verification Results
              const renderAtRiskVerification = () => {
                if (!showAtRiskVerification || atRiskVerificationResults.length === 0) return null;

                const allCorrect = atRiskVerificationResults.every(r => r.isCorrect);

                return (
                  <div className={`mt-4 p-4 rounded-xl ${isDark ? 'bg-white/[0.02] backdrop-blur-md border-white/[0.05]' : 'bg-gray-50/80 backdrop-blur-sm'} border ${isDark ? '' : theme.borderLight} ${isDark ? 'shadow-xl shadow-black/40' : 'shadow-lg'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5 text-[#F59E0B]" />
                        <h4 className={`text-base font-semibold ${theme.text}`}>Kết quả kiểm tra At-risk</h4>
                        {allCorrect ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-500 text-xs font-medium">
                            <CheckCircle2 className="w-3 h-3" />
                            Tất cả đúng
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 text-xs font-medium">
                            <AlertCircle className="w-3 h-3" />
                            Có sai lệch
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setShowAtRiskVerification(false)}
                        className={`p-1.5 rounded-lg ${theme.buttonBg} ${theme.textMuted} hover:${theme.text} transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B]`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      {atRiskVerificationResults.map((result, idx) => {
                        const getCopyText = () => {
                          const renewBreakdown = `Renew = ${result.renewExtend} (extend) + ${result.renewCreate} (create) + ${result.upsellCreate} (upsell) = ${result.renewTotal}`;
                          const formula = `At-risk = ${result.renewTotal} (renew) + ${result.quit} (quit) + ${result.gap} (gap) = ${result.expectedAtRisk}`;
                          const comparison = `Kỳ vọng: ${result.expectedAtRisk} | Thực tế: ${result.actualAtRisk}${!result.isCorrect ? ` (Chênh lệch: ${result.difference > 0 ? '+' : ''}${result.difference})` : ''}`;
                          return `[${result.week}] ${result.isCorrect ? '✓' : '✗'}\n${renewBreakdown}\n${formula}\n${comparison}`;
                        };

                        const handleCopy = async () => {
                          try {
                            await navigator.clipboard.writeText(getCopyText());
                            setCopiedIndex(1000 + idx); // Use offset to distinguish from paying verification
                            setTimeout(() => setCopiedIndex(null), 2000);
                          } catch (err) {
                            console.error('Failed to copy:', err);
                          }
                        };

                        return (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border ${result.isCorrect
                              ? isDark ? 'border-green-500/30 bg-green-500/5' : 'border-green-200 bg-green-50'
                              : isDark ? 'border-red-500/30 bg-red-500/5' : 'border-red-200 bg-red-50'
                              }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {result.isCorrect ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                )}
                                <span className={`font-medium ${theme.text}`}>{result.week}</span>
                              </div>
                              <button
                                onClick={handleCopy}
                                className={`p-1.5 rounded-lg transition-colors ${copiedIndex === 1000 + idx
                                  ? 'bg-green-500/20 text-green-500'
                                  : 'bg-[#F59E0B] text-white hover:bg-[#DC7A02]'
                                  }`}
                                title="Copy log"
                              >
                                {copiedIndex === 1000 + idx ? (
                                  <Check className="w-3.5 h-3.5" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>

                            <div className={`text-sm ${theme.textMuted} space-y-1`}>
                              {/* Renew breakdown */}
                              <p className="font-mono text-xs">
                                <span className={theme.textMuted}>Renew = </span>
                                <span className="text-blue-500">{result.renewExtend}</span>
                                <span className={`text-xs ${theme.textMuted}`}> (extend)</span>
                                <span className="mx-1">+</span>
                                <span className="text-purple-500">{result.renewCreate}</span>
                                <span className={`text-xs ${theme.textMuted}`}> (create)</span>
                                <span className="mx-1">+</span>
                                <span className="text-pink-500">{result.upsellCreate}</span>
                                <span className={`text-xs ${theme.textMuted}`}> (upsell)</span>
                                <span className="mx-1">=</span>
                                <strong className="text-green-500">{result.renewTotal}</strong>
                              </p>
                              {/* At-risk formula */}
                              <p className="font-mono">
                                <span className={theme.textMuted}>At-risk = </span>
                                <span className="text-green-500">{result.renewTotal}</span>
                                <span className={`text-xs ${theme.textMuted}`}> (renew)</span>
                                <span className="mx-1">+</span>
                                <span className="text-red-500">{result.quit}</span>
                                <span className={`text-xs ${theme.textMuted}`}> (quit)</span>
                                <span className="mx-1">+</span>
                                <span className="text-orange-500">{result.gap}</span>
                                <span className={`text-xs ${theme.textMuted}`}> (gap)</span>
                                <span className="mx-1">=</span>
                                <strong className={result.isCorrect ? 'text-green-500' : 'text-yellow-500'}>
                                  {result.expectedAtRisk}
                                </strong>
                              </p>
                              <p>
                                📊 Kỳ vọng: <strong>{result.expectedAtRisk}</strong>
                                {' | '}
                                Thực tế: <strong className={theme.text}>{result.actualAtRisk}</strong>
                                {!result.isCorrect && (
                                  <span className="text-red-500 ml-2">
                                    (Chênh lệch: {result.difference > 0 ? '+' : ''}{result.difference})
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className={`mt-4 pt-3 border-t ${theme.borderLight} text-xs ${theme.textMuted}`}>
                      <p>
                        <strong>Công thức:</strong> At-risk = Renew + Quit + Gap
                        <span className="ml-3">(Renew = RenewExtend + RenewCreate + UpsellCreate)</span>
                      </p>
                    </div>
                  </div>
                );
              };

              // Render Chart Component
              const renderChart = (
                metrics: MetricDef[],
                tableType: 'paying' | 'atRisk',
                onClose: () => void
              ) => {
                const chartData = prepareChartData(metrics, tableType);
                const colors = tableType === 'paying' ? CHART_COLORS.paying : CHART_COLORS.atRisk;

                // For at-risk: show all as stacked
                const allMetrics = tableType === 'atRisk'
                  ? metrics.filter(m => !m.isHighlight)
                  : [];

                // For At-risk, always prepare all 3 data types
                const areaChartData = tableType === 'atRisk' ? prepareAreaChartData() : [];
                const pieChartData = tableType === 'atRisk' ? prepareLatestPieChartData() : [];

                // Load studio breakdown data if needed
                if (tableType === 'atRisk' && (chartVisibility.studioBreakdown || chartVisibility.quitDaysChart) && studioBreakdownData.length === 0) {
                  loadStudioBreakdown();
                }

                // Toggle chart visibility
                const toggleChartVisibility = (chartKey: 'barChart' | 'areaChart' | 'pieChart' | 'studioBreakdown' | 'quitDaysChart') => {
                  const newVisibility = { ...chartVisibility, [chartKey]: !chartVisibility[chartKey] };
                  setChartVisibility(newVisibility);
                  localStorage.setItem('atRiskChartVisibility', JSON.stringify(newVisibility));

                  // Load studio data when enabling studioBreakdown or quitDaysChart
                  if ((chartKey === 'studioBreakdown' || chartKey === 'quitDaysChart') && !chartVisibility[chartKey] && tableType === 'atRisk') {
                    loadStudioBreakdown();
                  }
                };

                // Select all charts
                const selectAllCharts = () => {
                  const allVisible = { barChart: true, areaChart: true, pieChart: true, studioBreakdown: true, quitDaysChart: true };
                  setChartVisibility(allVisible);
                  localStorage.setItem('atRiskChartVisibility', JSON.stringify(allVisible));

                  // Load studio data when selecting all
                  if (tableType === 'atRisk') {
                    loadStudioBreakdown();
                  }
                };

                const selectAllPayingCharts = () => {
                  const allVisible = { stackedAreaChart: true, studioBreakdown: true };
                  setPayingChartVisibility(allVisible);
                  localStorage.setItem('payingChartVisibility', JSON.stringify(allVisible));
                };

                // Toggle Paying chart visibility
                const togglePayingChartVisibility = (chartKey: 'stackedAreaChart' | 'studioBreakdown') => {
                  const newVisibility = { ...payingChartVisibility, [chartKey]: !payingChartVisibility[chartKey] };
                  setPayingChartVisibility(newVisibility);
                  localStorage.setItem('payingChartVisibility', JSON.stringify(newVisibility));

                  // Load studio data when enabling studioBreakdown
                  if (chartKey === 'studioBreakdown' && !payingChartVisibility[chartKey] && tableType === 'paying') {
                    loadPayingStudioBreakdown();
                  }
                };

                // Load paying studio breakdown data if needed
                if (tableType === 'paying' && payingChartVisibility.studioBreakdown && payingStudioBreakdownData.length === 0) {
                  loadPayingStudioBreakdown();
                }

                // Prepare data for Paying charts
                const payingStackedAreaData = preparePayingStackedAreaData();

                return (
                  <div className={`mt-4 p-4 rounded-xl ${isDark ? 'bg-white/[0.02] backdrop-blur-md border-white/[0.05]' : 'bg-gray-50'} border ${isDark ? '' : theme.borderLight} shadow-lg`}>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className={`text-base font-semibold ${theme.text}`}>
                        {tableType === 'paying' ? 'Visualization for Paying Members' : 'Visualization for At-risk Members'}
                      </h4>
                      <div className="flex items-center gap-2">
                        {tableType === 'paying' && (
                          <div className="relative chart-settings-dropdown">
                            <button
                              onClick={() => setShowPayingChartSettings(!showPayingChartSettings)}
                              className={`p-1.5 rounded-lg ${theme.buttonBg} ${theme.textMuted} hover:${theme.text} transition-colors`}
                              title="Cài đặt hiển thị chart"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                            {showPayingChartSettings && (
                              <div className={`absolute right-0 mt-2 w-64 rounded-lg shadow-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} z-50`}>
                                <div className={`p-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                  <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${theme.text}`}>Hiển thị biểu đồ</span>
                                    <button
                                      onClick={selectAllPayingCharts}
                                      className="text-xs text-[#F59E0B] hover:text-[#FBBF24]"
                                    >
                                      Chọn tất cả
                                    </button>
                                  </div>
                                </div>
                                <div className="p-2">
                                  <label className={`flex items-center gap-2 p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} cursor-pointer`}>
                                    <input
                                      type="checkbox"
                                      checked={payingChartVisibility.stackedAreaChart}
                                      onChange={() => togglePayingChartVisibility('stackedAreaChart')}
                                      className="w-4 h-4 text-[#F59E0B] rounded focus:ring-[#F59E0B]"
                                    />
                                    <span className={`text-sm ${theme.text}`}>Stacked Area Chart</span>
                                  </label>
                                  <label className={`flex items-center gap-2 p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} cursor-pointer`}>
                                    <input
                                      type="checkbox"
                                      checked={payingChartVisibility.studioBreakdown}
                                      onChange={() => togglePayingChartVisibility('studioBreakdown')}
                                      className="w-4 h-4 text-[#F59E0B] rounded focus:ring-[#F59E0B]"
                                    />
                                    <span className={`text-sm ${theme.text}`}>Phân bố theo Studio</span>
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {tableType === 'atRisk' && (
                          <div className="relative chart-settings-dropdown">
                            <button
                              onClick={() => setShowChartSettings(!showChartSettings)}
                              className={`p-1.5 rounded-lg ${theme.buttonBg} ${theme.textMuted} hover:${theme.text} transition-colors`}
                              title="Cài đặt hiển thị chart"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                            {showChartSettings && (
                              <div className={`absolute right-0 mt-2 w-56 rounded-lg shadow-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} z-50`}>
                                <div className={`p-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className={`text-sm font-medium ${theme.text}`}>Hiển thị biểu đồ</span>
                                    <button
                                      onClick={selectAllCharts}
                                      className="text-xs text-[#F59E0B] hover:text-[#FBBF24]"
                                    >
                                      Chọn tất cả
                                    </button>
                                  </div>
                                </div>
                                <div className="p-2">
                                  <label className={`flex items-center gap-2 p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} cursor-pointer`}>
                                    <input
                                      type="checkbox"
                                      checked={chartVisibility.barChart}
                                      onChange={() => toggleChartVisibility('barChart')}
                                      className="w-4 h-4 text-[#F59E0B] rounded focus:ring-[#F59E0B]"
                                    />
                                    <span className={`text-sm ${theme.text}`}>Số lượng (Bar Chart)</span>
                                  </label>
                                  <label className={`flex items-center gap-2 p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} cursor-pointer`}>
                                    <input
                                      type="checkbox"
                                      checked={chartVisibility.areaChart}
                                      onChange={() => toggleChartVisibility('areaChart')}
                                      className="w-4 h-4 text-[#F59E0B] rounded focus:ring-[#F59E0B]"
                                    />
                                    <span className={`text-sm ${theme.text}`}>Phần trăm theo thời gian (Area Chart)</span>
                                  </label>
                                  <label className={`flex items-center gap-2 p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} cursor-pointer`}>
                                    <input
                                      type="checkbox"
                                      checked={chartVisibility.pieChart}
                                      onChange={() => toggleChartVisibility('pieChart')}
                                      className="w-4 h-4 text-[#F59E0B] rounded focus:ring-[#F59E0B]"
                                    />
                                    <span className={`text-sm ${theme.text}`}>Phần trăm tổng quan (Pie Chart)</span>
                                  </label>
                                  <label className={`flex items-center gap-2 p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} cursor-pointer`}>
                                    <input
                                      type="checkbox"
                                      checked={chartVisibility.studioBreakdown}
                                      onChange={() => toggleChartVisibility('studioBreakdown')}
                                      className="w-4 h-4 text-[#F59E0B] rounded focus:ring-[#F59E0B]"
                                    />
                                    <span className={`text-sm ${theme.text}`}>Phân bố theo Studio</span>
                                  </label>
                                  <label className={`flex items-center gap-2 p-2 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} cursor-pointer`}>
                                    <input
                                      type="checkbox"
                                      checked={chartVisibility.quitDaysChart}
                                      onChange={() => toggleChartVisibility('quitDaysChart')}
                                      className="w-4 h-4 text-[#F59E0B] rounded focus:ring-[#F59E0B]"
                                    />
                                    <span className={`text-sm ${theme.text}`}>TB Số ngày ngưng tập</span>
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <button
                          onClick={onClose}
                          className={`p-1.5 rounded-lg ${theme.buttonBg} ${theme.textMuted} hover:${theme.text} transition-colors`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {tableType === 'atRisk' ? (
                      <div className="space-y-4">
                        {/* Row 1: Bar Chart + Area Chart + Pie Chart (3 columns) */}
                        {(chartVisibility.barChart || chartVisibility.areaChart || chartVisibility.pieChart) && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            {/* Column 1: Bar Chart (Số lượng) */}
                            {chartVisibility.barChart && (
                              <div className="flex flex-col">
                                <h5 className={`text-sm font-medium ${theme.text} mb-2 text-center`}>Số lượng</h5>
                                <ResponsiveContainer width="100%" height={250}>
                                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                    <XAxis
                                      dataKey="week"
                                      tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                                      angle={-15}
                                      textAnchor="end"
                                      height={50}
                                    />
                                    <YAxis tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} />
                                    <Tooltip
                                      contentStyle={{
                                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#fff',
                                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : '#e5e7eb'}`,
                                        borderRadius: '12px',
                                        color: isDark ? '#fff' : '#000',
                                        backdropFilter: 'blur(16px)',
                                        boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.6)' : '0 4px 16px rgba(0, 0, 0, 0.1)'
                                      }}
                                      content={({ active, payload, label }) => {
                                        if (!active || !payload || payload.length === 0) return null;
                                        const data = payload[0]?.payload;
                                        const totalValue = data?.['At-risk'];

                                        return (
                                          <div className={`p-3 rounded-xl shadow-2xl ${isDark ? 'bg-white/[0.08] backdrop-blur-xl border border-white/[0.15]' : 'bg-white border border-gray-200'}`}>
                                            <p className={`font-semibold mb-2 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</p>
                                            {payload.map((entry: any, index: number) => (
                                              <p key={index} style={{ color: entry.color }} className="text-sm font-medium">
                                                {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
                                              </p>
                                            ))}
                                            {totalValue !== undefined && (
                                              <p className={`mt-2 pt-2 border-t ${isDark ? 'border-white/20' : 'border-gray-200'} font-semibold text-[#F59E0B] text-sm`}>
                                                At-risk: {totalValue.toLocaleString()}
                                              </p>
                                            )}
                                          </div>
                                        );
                                      }}
                                    />
                                    <Legend
                                      wrapperStyle={{ fontSize: '10px' }}
                                      formatter={(value) => <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '10px' }}>{value}</span>}
                                    />
                                    {allMetrics.map((metric, idx) => (
                                      <Bar
                                        key={metric.label}
                                        dataKey={metric.label}
                                        stackId="a"
                                        fill={colors[metric.label as keyof typeof colors] || '#888'}
                                        radius={idx === allMetrics.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                      />
                                    ))}
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            )}

                            {/* Column 2: Area Chart (Phần trăm theo thời gian) - BarChart khi chỉ có 1 điểm dữ liệu */}
                            {chartVisibility.areaChart && (
                              <div className="flex flex-col">
                                <h5 className={`text-sm font-medium ${theme.text} mb-2 text-center`}>Phần trăm theo thời gian</h5>
                                <ResponsiveContainer width="100%" height={250}>
                                  {areaChartData.length === 1 ? (
                                    <BarChart data={areaChartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                      <XAxis
                                        dataKey="week"
                                        tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                                        angle={-15}
                                        textAnchor="end"
                                        height={50}
                                      />
                                      <YAxis
                                        tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                                        domain={[0, 100]}
                                        tickFormatter={(value) => `${value}%`}
                                      />
                                      <Tooltip
                                        contentStyle={{
                                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#fff',
                                          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : '#e5e7eb'}`,
                                          borderRadius: '12px',
                                          color: isDark ? '#fff' : '#000',
                                          backdropFilter: 'blur(16px)',
                                          boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.6)' : '0 4px 16px rgba(0, 0, 0, 0.1)'
                                        }}
                                        content={({ active, payload, label }) => {
                                          if (!active || !payload || payload.length === 0) return null;
                                          const data = payload[0]?.payload;

                                          return (
                                            <div className={`p-3 rounded-xl shadow-2xl ${isDark ? 'bg-white/[0.08] backdrop-blur-xl border border-white/[0.15]' : 'bg-white border border-gray-200'}`}>
                                              <p className={`font-semibold mb-2 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</p>
                                              {payload.map((entry: any, index: number) => (
                                                <p key={index} style={{ color: entry.color }} className="text-sm font-medium">
                                                  {entry.name}: {typeof entry.value === 'number' ? `${entry.value.toFixed(1)}%` : entry.value}
                                                </p>
                                              ))}
                                              {data?._atRiskTotal !== undefined && (
                                                <div className={`mt-2 pt-2 border-t ${isDark ? 'border-white/20' : 'border-gray-200'}`}>
                                                  <p className={`text-xs ${theme.textMuted} mb-1`}>Số lượng:</p>
                                                  <p className="text-sm font-semibold text-[#F59E0B]">
                                                    Renew: {data._renewTotal} | Quit: {data._quit} | Gap: {data._gap}
                                                  </p>
                                                  <p className="text-sm font-semibold text-[#F59E0B]">
                                                    At-risk: {data._atRiskTotal}
                                                  </p>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }}
                                      />
                                      <Legend
                                        wrapperStyle={{ fontSize: '10px' }}
                                        formatter={(value) => <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '10px' }}>{value}</span>}
                                      />
                                      <Bar dataKey="Renew Members" fill="#10b981" name="Renew Members" radius={[4, 4, 0, 0]} />
                                      <Bar dataKey="Quit Members" fill="#ef4444" name="Quit Members" radius={[4, 4, 0, 0]} />
                                      <Bar dataKey="Gap Members" fill="#f59e0b" name="Gap Members" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                  ) : (
                                    <AreaChart data={areaChartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                      <defs>
                                        <linearGradient id="colorRenew" x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                          <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                                        </linearGradient>
                                        <linearGradient id="colorQuit" x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1} />
                                        </linearGradient>
                                        <linearGradient id="colorGap" x1="0" y1="0" x2="0" y2="1">
                                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1} />
                                        </linearGradient>
                                      </defs>
                                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                      <XAxis
                                        dataKey="week"
                                        tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                                        angle={-15}
                                        textAnchor="end"
                                        height={50}
                                      />
                                      <YAxis
                                        tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                                        domain={[0, 100]}
                                        tickFormatter={(value) => `${value}%`}
                                      />
                                      <Tooltip
                                        contentStyle={{
                                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#fff',
                                          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : '#e5e7eb'}`,
                                          borderRadius: '12px',
                                          color: isDark ? '#fff' : '#000',
                                          backdropFilter: 'blur(16px)',
                                          boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.6)' : '0 4px 16px rgba(0, 0, 0, 0.1)'
                                        }}
                                        content={({ active, payload, label }) => {
                                          if (!active || !payload || payload.length === 0) return null;
                                          const data = payload[0]?.payload;

                                          return (
                                            <div className={`p-3 rounded-xl shadow-2xl ${isDark ? 'bg-white/[0.08] backdrop-blur-xl border border-white/[0.15]' : 'bg-white border border-gray-200'}`}>
                                              <p className={`font-semibold mb-2 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</p>
                                              {payload.map((entry: any, index: number) => (
                                                <p key={index} style={{ color: entry.color }} className="text-sm font-medium">
                                                  {entry.name}: {typeof entry.value === 'number' ? `${entry.value.toFixed(1)}%` : entry.value}
                                                </p>
                                              ))}
                                              {data?._atRiskTotal !== undefined && (
                                                <div className={`mt-2 pt-2 border-t ${isDark ? 'border-white/20' : 'border-gray-200'}`}>
                                                  <p className={`text-xs ${theme.textMuted} mb-1`}>Số lượng:</p>
                                                  <p className="text-sm font-semibold text-[#F59E0B]">
                                                    Renew: {data._renewTotal} | Quit: {data._quit} | Gap: {data._gap}
                                                  </p>
                                                  <p className="text-sm font-semibold text-[#F59E0B]">
                                                    At-risk: {data._atRiskTotal}
                                                  </p>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }}
                                      />
                                      <Legend
                                        wrapperStyle={{ fontSize: '10px' }}
                                        formatter={(value) => <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '10px' }}>{value}</span>}
                                      />
                                      <Area
                                        type="monotone"
                                        dataKey="Renew Members"
                                        stackId="1"
                                        stroke="#10b981"
                                        fill="url(#colorRenew)"
                                        name="Renew Members"
                                      />
                                      <Area
                                        type="monotone"
                                        dataKey="Quit Members"
                                        stackId="1"
                                        stroke="#ef4444"
                                        fill="url(#colorQuit)"
                                        name="Quit Members"
                                      />
                                      <Area
                                        type="monotone"
                                        dataKey="Gap Members"
                                        stackId="1"
                                        stroke="#f59e0b"
                                        fill="url(#colorGap)"
                                        name="Gap Members"
                                      />
                                    </AreaChart>
                                  )}
                                </ResponsiveContainer>
                              </div>
                            )}

                            {/* Pie Chart (Phần trăm tổng quan - latest week) */}
                            {chartVisibility.pieChart && (
                              <div className="flex flex-col">
                                <h5 className={`text-sm font-medium ${theme.text} mb-2 text-center`}>
                                  Phần trăm tổng quan ({weeks[weeks.length - 1]})
                                </h5>
                                <ResponsiveContainer width="100%" height={250}>
                                  <PieChart>
                                    <Pie
                                      data={pieChartData}
                                      cx="50%"
                                      cy="50%"
                                      labelLine={false}
                                      label={({ name, value }) => `${name}: ${value}%`}
                                      outerRadius={85}
                                      fill="#8884d8"
                                      dataKey="value"
                                    >
                                      {pieChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                      ))}
                                    </Pie>
                                    <Tooltip
                                      contentStyle={{
                                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#fff',
                                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : '#e5e7eb'}`,
                                        borderRadius: '12px',
                                        color: isDark ? '#fff' : '#000',
                                        backdropFilter: 'blur(16px)',
                                        boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.6)' : '0 4px 16px rgba(0, 0, 0, 0.1)'
                                      }}
                                      content={({ active, payload }) => {
                                        if (!active || !payload || payload.length === 0) return null;
                                        const data = payload[0]?.payload;

                                        return (
                                          <div className={`p-3 rounded-xl shadow-2xl ${isDark ? 'bg-white/[0.08] backdrop-blur-xl border border-white/[0.15]' : 'bg-white border border-gray-200'}`}>
                                            <p className={`font-semibold mb-2 text-sm`} style={{ color: data.color }}>
                                              {data.name}
                                            </p>
                                            <p className="text-sm font-medium">
                                              Phần trăm: {data.value}%
                                            </p>
                                            <p className="text-sm font-medium">
                                              Số lượng: {data.count.toLocaleString()}
                                            </p>
                                          </div>
                                        );
                                      }}
                                    />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Row 2: Studio Breakdown Chart + Table (2 columns) */}
                        {chartVisibility.studioBreakdown && studioBreakdownData.length > 0 && (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Studio Breakdown Chart */}
                            <div className="flex flex-col">
                              <h5 className={`text-sm font-medium ${theme.text} mb-2 text-center`}>
                                Phân bố theo Studio ({weeks[weeks.length - 1]})
                              </h5>
                              <ResponsiveContainer width="100%" height={280}>
                                <BarChart
                                  data={studioBreakdownData.map((item, idx) => ({
                                    ...item,
                                    color: ['#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7', '#eab308', '#22c55e', '#0ea5e9'][idx % 15]
                                  }))}
                                  margin={{ top: 10, right: 10, left: 0, bottom: 60 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                  <XAxis
                                    dataKey="studioName"
                                    tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 9 }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    interval={0}
                                  />
                                  <YAxis tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: isDark ? '#1f2937' : '#fff',
                                      border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                                      borderRadius: '8px',
                                      color: isDark ? '#fff' : '#000'
                                    }}
                                    content={({ active, payload }) => {
                                      if (!active || !payload || payload.length === 0) return null;
                                      const data = payload[0]?.payload;
                                      const totalMembers = studioBreakdownData.reduce((sum, item) => sum + item.atRiskCount, 0);
                                      const percentage = ((data.atRiskCount / totalMembers) * 100).toFixed(1);
                                      const quitRate = data.atRiskCount > 0 ? ((data.quitCount / data.atRiskCount) * 100).toFixed(1) : '0.0';
                                      const renewRate = data.atRiskCount > 0 ? ((data.renewCount / data.atRiskCount) * 100).toFixed(1) : '0.0';
                                      return (
                                        <div className={`p-2 rounded-lg shadow-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                          <p className={`font-semibold mb-1 text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {data.studioName}
                                          </p>
                                          <p className="text-xs" style={{ color: data.color }}>
                                            At-risk: <strong>{data.atRiskCount}</strong> ({percentage}%)
                                          </p>
                                          <p className="text-xs text-red-500">
                                            Quit: {data.quitCount} ({quitRate}%)
                                          </p>
                                          <p className="text-xs text-green-500">
                                            Renew: {data.renewCount} ({renewRate}%)
                                          </p>
                                        </div>
                                      );
                                    }}
                                  />
                                  <Bar dataKey="atRiskCount" radius={[4, 4, 0, 0]}>
                                    {studioBreakdownData.map((_entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={['#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7', '#eab308', '#22c55e', '#0ea5e9'][index % 15]}
                                      />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>

                            {/* Table with detailed breakdown */}
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className={`text-sm font-medium ${theme.text}`}>Chi tiết theo Studio</h5>
                                <button
                                  onClick={() => {
                                    const headers = ['Studio', 'At-risk', 'Quit', 'Renew', 'Gap', 'Tỷ lệ Quit (%)', 'Tỷ lệ Renew (%)'];
                                    const csvContent = [
                                      headers.join(','),
                                      ...studioBreakdownData.map(item => {
                                        const quitRate = item.atRiskCount > 0 ? ((item.quitCount / item.atRiskCount) * 100).toFixed(1) : '0.0';
                                        const renewRate = item.atRiskCount > 0 ? ((item.renewCount / item.atRiskCount) * 100).toFixed(1) : '0.0';
                                        return [
                                          `"${item.studioName}"`,
                                          item.atRiskCount,
                                          item.quitCount,
                                          item.renewCount,
                                          item.gapCount,
                                          quitRate,
                                          renewRate
                                        ].join(',');
                                      })
                                    ].join('\n');
                                    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                                    const link = document.createElement('a');
                                    link.href = URL.createObjectURL(blob);
                                    link.download = `studio_breakdown_${weeks[weeks.length - 1]}_${versionId}.csv`;
                                    link.click();
                                  }}
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                                >
                                  <Download className="w-3 h-3" />
                                  Export CSV
                                </button>
                              </div>
                              <div className={`rounded-lg border ${theme.borderLight} overflow-hidden`} style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <table className="w-full text-xs">
                                  <thead className={`sticky top-0 ${theme.tableHeader} z-10`}>
                                    <tr>
                                      <th className={`px-3 py-2 text-left font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Studio
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        At-risk
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Quit
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Renew
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Gap
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Tỷ lệ Quit
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Tỷ lệ Renew
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className={`divide-y ${theme.borderLight}`}>
                                    {studioBreakdownData.map((item, idx) => {
                                      const quitRate = item.atRiskCount > 0 ? ((item.quitCount / item.atRiskCount) * 100).toFixed(1) : '0.0';
                                      const renewRate = item.atRiskCount > 0 ? ((item.renewCount / item.atRiskCount) * 100).toFixed(1) : '0.0';
                                      const colorIndex = idx % 15;
                                      const studioColor = ['#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7', '#eab308', '#22c55e', '#0ea5e9'][colorIndex];
                                      return (
                                        <tr key={idx} className={theme.tableRow}>
                                          <td className={`px-3 py-2 ${theme.text}`}>
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-2 h-2 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: studioColor }}
                                              />
                                              <span className="text-xs truncate">{item.studioName}</span>
                                            </div>
                                          </td>
                                          <td className={`px-3 py-2 ${theme.textSecondary} text-center font-medium`}>
                                            {item.atRiskCount}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-red-500`}>
                                            {item.quitCount}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-green-500`}>
                                            {item.renewCount}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-yellow-500`}>
                                            {item.gapCount}
                                          </td>
                                          <td className={`px-3 py-2 text-center text-red-500`}>
                                            {quitRate}%
                                          </td>
                                          <td className={`px-3 py-2 text-center text-green-500`}>
                                            {renewRate}%
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    {/* Total row */}
                                    <tr className={`${theme.metricRow} font-semibold`}>
                                      <td className={`px-3 py-2 ${theme.text}`}>Tổng</td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {studioBreakdownData.reduce((sum, item) => sum + item.atRiskCount, 0)}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {studioBreakdownData.reduce((sum, item) => sum + item.quitCount, 0)}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {studioBreakdownData.reduce((sum, item) => sum + item.renewCount, 0)}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {studioBreakdownData.reduce((sum, item) => sum + item.gapCount, 0)}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {(() => {
                                          const totalAtRisk = studioBreakdownData.reduce((sum, item) => sum + item.atRiskCount, 0);
                                          const totalQuit = studioBreakdownData.reduce((sum, item) => sum + item.quitCount, 0);
                                          return totalAtRisk > 0 ? ((totalQuit / totalAtRisk) * 100).toFixed(1) : '0.0';
                                        })()}%
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {(() => {
                                          const totalAtRisk = studioBreakdownData.reduce((sum, item) => sum + item.atRiskCount, 0);
                                          const totalRenew = studioBreakdownData.reduce((sum, item) => sum + item.renewCount, 0);
                                          return totalAtRisk > 0 ? ((totalRenew / totalAtRisk) * 100).toFixed(1) : '0.0';
                                        })()}%
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Row 3: Quit Days Chart + Table (2 columns) */}
                        {chartVisibility.quitDaysChart && quitDaysBreakdownData.length > 0 && (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Quit Days Chart */}
                            <div className="flex flex-col">
                              <h5 className={`text-sm font-medium ${theme.text} mb-2 text-center`}>
                                Trung bình số ngày ngưng tập cho đến khi quit (Days) - {weeks[weeks.length - 1]}
                              </h5>
                              <div style={{ width: '100%', height: 280 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={quitDaysBreakdownData.map((item, idx) => ({
                                      ...item,
                                      color: ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899'][idx % 15]
                                    }))}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 60 }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                    <XAxis
                                      dataKey="studioName"
                                      tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 9 }}
                                      angle={-45}
                                      textAnchor="end"
                                      height={80}
                                      interval={0}
                                    />
                                    <YAxis tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }} />
                                    <Tooltip
                                      contentStyle={{
                                        backgroundColor: isDark ? '#1f2937' : '#fff',
                                        border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                                        borderRadius: '8px',
                                        color: isDark ? '#fff' : '#000'
                                      }}
                                      content={({ active, payload }) => {
                                        if (!active || !payload || payload.length === 0) return null;
                                        const data = payload[0]?.payload;
                                        return (
                                          <div className={`p-2 rounded-lg shadow-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                            <p className={`font-semibold mb-1 text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                              {data.studioName}
                                            </p>
                                            <p className="text-xs text-red-500">
                                              TB số ngày: <strong>{data.avgDaysFromLastCheckIn}</strong> ngày
                                            </p>
                                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                              Số members: {data.memberCount}
                                            </p>
                                          </div>
                                        );
                                      }}
                                    />
                                    <Bar dataKey="avgDaysFromLastCheckIn" name="Trung bình số ngày ngưng tập cho đến khi quit (Days)" radius={[4, 4, 0, 0]}>
                                      {quitDaysBreakdownData.map((_entry, index) => (
                                        <Cell
                                          key={`cell-${index}`}
                                          fill={['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899'][index % 15]}
                                        />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>

                            {/* Table with Studio and Average Days */}
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className={`text-sm font-medium ${theme.text}`}>Chi tiết trung bình số ngày ngưng tập cho đến khi quit</h5>
                                <button
                                  onClick={() => {
                                    const headers = ['Studio Name', 'TB (Days)', 'Min (Days)', 'Max (Days)'];
                                    const csvContent = [
                                      headers.join(','),
                                      ...quitDaysBreakdownData.map(item => [
                                        `"${item.studioName}"`,
                                        item.avgDaysFromLastCheckIn,
                                        item.minDays,
                                        item.maxDays
                                      ].join(','))
                                    ].join('\n');
                                    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                                    const link = document.createElement('a');
                                    link.href = URL.createObjectURL(blob);
                                    link.download = `quit_days_by_studio_${weeks[weeks.length - 1]}_${versionId}.csv`;
                                    link.click();
                                  }}
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                                >
                                  <Download className="w-3 h-3" />
                                  Export CSV
                                </button>
                              </div>
                              <div className={`rounded-lg border ${theme.borderLight} overflow-hidden`} style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <table className="w-full text-xs">
                                  <thead className={`sticky top-0 ${theme.tableHeader} z-10`}>
                                    <tr>
                                      <th className={`px-3 py-2 text-left font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Studio Name
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        TB (Days)
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Min (Days)
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Max (Days)
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className={`divide-y ${theme.borderLight}`}>
                                    {quitDaysBreakdownData.map((item, idx) => {
                                      const colorIndex = idx % 15;
                                      const studioColor = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#ec4899'][colorIndex];
                                      return (
                                        <tr key={idx} className={theme.tableRow}>
                                          <td className={`px-3 py-2 ${theme.text}`}>
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-2 h-2 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: studioColor }}
                                              />
                                              <span className="text-xs truncate">{item.studioName}</span>
                                            </div>
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-red-500`}>
                                            {item.avgDaysFromLastCheckIn}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-blue-500`}>
                                            {item.minDays}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-orange-500`}>
                                            {item.maxDays}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    {/* Average row */}
                                    <tr className={`${theme.metricRow} font-semibold`}>
                                      <td className={`px-3 py-2 ${theme.text}`}>Trung bình</td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {(() => {
                                          const totalDays = quitDaysBreakdownData.reduce((sum, item) => sum + item.avgDaysFromLastCheckIn * item.memberCount, 0);
                                          const totalMembers = quitDaysBreakdownData.reduce((sum, item) => sum + item.memberCount, 0);
                                          return totalMembers > 0 ? Math.round(totalDays / totalMembers) : 0;
                                        })()}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {quitDaysBreakdownData.length > 0 ? Math.round(quitDaysBreakdownData.reduce((sum, item) => sum + item.minDays, 0) / quitDaysBreakdownData.length) : 0}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {quitDaysBreakdownData.length > 0 ? Math.round(quitDaysBreakdownData.reduce((sum, item) => sum + item.maxDays, 0) / quitDaysBreakdownData.length) : 0}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`grid gap-4 ${(() => {
                        const visibleCount = [payingChartVisibility.stackedAreaChart, payingChartVisibility.studioBreakdown].filter(Boolean).length;
                        if (visibleCount === 2) return 'grid-cols-1 md:grid-cols-2';
                        return 'grid-cols-1';
                      })()
                        }`}>
                        {/* Stacked Area Chart - Thành phần tạo nên Paying Members */}
                        {payingChartVisibility.stackedAreaChart && (
                          <div className="flex flex-col">
                            <h5 className={`text-sm font-medium ${theme.text} mb-2 text-center`}>Thành phần tạo nên Paying Members</h5>
                            <ResponsiveContainer width="100%" height={350}>
                              {payingStackedAreaData.length === 1 ? (
                                <BarChart data={payingStackedAreaData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                  <XAxis
                                    dataKey="week"
                                    tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                                    angle={-15}
                                    textAnchor="end"
                                    height={60}
                                  />
                                  <YAxis tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 11 }} />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: isDark ? '#1f2937' : '#fff',
                                      border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                                      borderRadius: '8px',
                                    }}
                                    content={({ active, payload, label }) => {
                                      if (!active || !payload) return null;
                                      return (
                                        <div className={`p-3 rounded-lg shadow-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                          <p className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</p>
                                          {payload.map((entry: any, index: number) => (
                                            <p key={index} style={{ color: entry.color }} className="text-sm">
                                              {entry.name}: {entry.value?.toLocaleString()}
                                            </p>
                                          ))}
                                        </div>
                                      );
                                    }}
                                  />
                                  <Legend
                                    wrapperStyle={{ fontSize: '11px' }}
                                    formatter={(value) => <span style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>{value}</span>}
                                  />
                                  <Bar dataKey="New Active" fill="#10b981" radius={[4, 4, 0, 0]} />
                                  <Bar dataKey="Returning" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                  <Bar dataKey="Quit" fill="#ef4444" radius={[4, 4, 0, 0]} />
                                  <Bar dataKey="Temporary Left" fill="#f97316" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              ) : (
                                <AreaChart data={payingStackedAreaData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                  <XAxis
                                    dataKey="week"
                                    tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                                    angle={-15}
                                    textAnchor="end"
                                    height={60}
                                  />
                                  <YAxis tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 11 }} />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: isDark ? '#1f2937' : '#fff',
                                      border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                                      borderRadius: '8px',
                                    }}
                                    content={({ active, payload, label }) => {
                                      if (!active || !payload) return null;
                                      return (
                                        <div className={`p-3 rounded-lg shadow-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                          <p className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</p>
                                          {payload.map((entry: any, index: number) => (
                                            <p key={index} style={{ color: entry.color }} className="text-sm">
                                              {entry.name}: {entry.value?.toLocaleString()}
                                            </p>
                                          ))}
                                        </div>
                                      );
                                    }}
                                  />
                                  <Legend
                                    wrapperStyle={{ fontSize: '11px' }}
                                    formatter={(value) => <span style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>{value}</span>}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="New Active"
                                    stackId="1"
                                    stroke="#10b981"
                                    fill="#10b981"
                                    fillOpacity={0.8}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="Returning"
                                    stackId="1"
                                    stroke="#3b82f6"
                                    fill="#3b82f6"
                                    fillOpacity={0.8}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="Quit"
                                    stackId="2"
                                    stroke="#ef4444"
                                    fill="#ef4444"
                                    fillOpacity={0.8}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="Temporary Left"
                                    stackId="2"
                                    stroke="#f97316"
                                    fill="#f97316"
                                    fillOpacity={0.8}
                                  />
                                </AreaChart>
                              )}
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* Studio Breakdown - Paying Members */}
                        {payingChartVisibility.studioBreakdown && (
                          <div className="flex flex-col">
                            <h5 className={`text-sm font-medium ${theme.text} mb-2 text-center`}>Phân bố theo Studio</h5>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart data={payingStudioBreakdownData} margin={{ top: 10, right: 10, left: 0, bottom: 80 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                <XAxis
                                  dataKey="studioName"
                                  tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 10 }}
                                  angle={-45}
                                  textAnchor="end"
                                  height={80}
                                />
                                <YAxis tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 11 }} />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: isDark ? '#1f2937' : '#fff',
                                    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                                    borderRadius: '8px',
                                  }}
                                  content={({ active, payload }) => {
                                    if (!active || !payload || payload.length === 0) return null;
                                    const data = payload[0]?.payload;
                                    return (
                                      <div className={`p-3 rounded-lg shadow-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                        <p className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.studioName}</p>
                                        <p className="text-sm text-cyan-500">
                                          Paying Members: {data.payingCount?.toLocaleString()}
                                        </p>
                                        <p className="text-sm text-green-500">
                                          New Active: {data.newActiveCount?.toLocaleString()}
                                        </p>
                                        <p className="text-sm text-blue-500">
                                          Returning: {data.returningCount?.toLocaleString()}
                                        </p>
                                        <p className="text-sm text-red-500">
                                          Quit: {data.quitCount?.toLocaleString()}
                                        </p>
                                        <p className="text-sm text-orange-500">
                                          Temporary Left: {data.temporaryLeftCount?.toLocaleString()}
                                        </p>
                                      </div>
                                    );
                                  }}
                                />
                                <Bar dataKey="payingCount" radius={[4, 4, 0, 0]}>
                                  {payingStudioBreakdownData.map((_entry, index) => {
                                    const colorIndex = index % 15;
                                    const color = ['#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7', '#eab308', '#22c55e', '#0ea5e9'][colorIndex];
                                    return <Cell key={`cell-${index}`} fill={color} />;
                                  })}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>

                            {/* Table with detailed breakdown */}
                            <div className="mt-4">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className={`text-sm font-medium ${theme.text}`}>Chi tiết theo Studio</h5>
                                <button
                                  onClick={() => {
                                    const headers = ['Studio', 'Paying Members', 'New Active', 'Returning', 'Quit', 'Temporary Left'];
                                    const csvContent = [
                                      headers.join(','),
                                      ...payingStudioBreakdownData.map(item => {
                                        return [
                                          `"${item.studioName}"`,
                                          item.payingCount,
                                          item.newActiveCount,
                                          item.returningCount,
                                          item.quitCount,
                                          item.temporaryLeftCount
                                        ].join(',');
                                      })
                                    ].join('\n');
                                    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                                    const link = document.createElement('a');
                                    link.href = URL.createObjectURL(blob);
                                    link.download = `paying_studio_breakdown_${weeks[weeks.length - 1]}_${versionId}.csv`;
                                    link.click();
                                  }}
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                                >
                                  <Download className="w-3 h-3" />
                                  Export CSV
                                </button>
                              </div>
                              <div className={`rounded-lg border ${theme.borderLight} overflow-auto`} style={{ maxHeight: '300px' }}>
                                <table className="w-full text-xs min-w-max">
                                  <thead className={`sticky top-0 ${theme.tableHeader} z-10`}>
                                    <tr>
                                      <th className={`px-3 py-2 text-left font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Studio
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Paying
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        New Active
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Returning
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Quit
                                      </th>
                                      <th className={`px-3 py-2 text-center font-semibold ${theme.textMuted} border-b ${theme.borderLight} uppercase tracking-wider`}>
                                        Temp Left
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className={`divide-y ${theme.borderLight}`}>
                                    {payingStudioBreakdownData.map((item, idx) => {
                                      const colorIndex = idx % 15;
                                      const studioColor = ['#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7', '#eab308', '#22c55e', '#0ea5e9'][colorIndex];
                                      return (
                                        <tr key={idx} className={theme.tableRow}>
                                          <td className={`px-3 py-2 ${theme.text}`}>
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-2 h-2 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: studioColor }}
                                              />
                                              <span className="text-xs truncate">{item.studioName}</span>
                                            </div>
                                          </td>
                                          <td className={`px-3 py-2 ${theme.textSecondary} text-center font-medium`}>
                                            {item.payingCount}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-green-500`}>
                                            {item.newActiveCount}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-blue-500`}>
                                            {item.returningCount}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-red-500`}>
                                            {item.quitCount}
                                          </td>
                                          <td className={`px-3 py-2 text-center font-medium text-orange-500`}>
                                            {item.temporaryLeftCount}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    {/* Total row */}
                                    <tr className={`${theme.metricRow} font-semibold`}>
                                      <td className={`px-3 py-2 ${theme.text}`}>Tổng</td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {payingStudioBreakdownData.reduce((sum, item) => sum + item.payingCount, 0)}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {payingStudioBreakdownData.reduce((sum, item) => sum + item.newActiveCount, 0)}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {payingStudioBreakdownData.reduce((sum, item) => sum + item.returningCount, 0)}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {payingStudioBreakdownData.reduce((sum, item) => sum + item.quitCount, 0)}
                                      </td>
                                      <td className={`px-3 py-2 ${theme.text} text-center`}>
                                        {payingStudioBreakdownData.reduce((sum, item) => sum + item.temporaryLeftCount, 0)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}


                    {/* AI Insights Section */}
                    {renderInsights(tableType)}
                  </div>
                );
              };

              // Generate AI Insights (Rule-based)
              const generateInsights = (tableType: 'paying' | 'atRisk') => {
                if (weeks.length < 2) return null;

                const insights: {
                  type: 'success' | 'warning' | 'info' | 'trend';
                  title: string;
                  message: string;
                }[] = [];

                if (tableType === 'paying') {
                  // Paying Members Analysis
                  const firstWeek = weeks[0];
                  const lastWeek = weeks[weeks.length - 1];
                  const firstPaying = getValue('Paying members', firstWeek);
                  const lastPaying = getValue('Paying members', lastWeek);
                  const growth = lastPaying - firstPaying;
                  const growthPercent = ((growth / firstPaying) * 100).toFixed(1);

                  // Trend Analysis
                  if (growth > 0) {
                    insights.push({
                      type: 'success',
                      title: '📈 Xu hướng tích cực',
                      message: `Paying Members tăng ${growth.toLocaleString()} thành viên (${growthPercent}%) trong ${weeks.length} tuần. Từ ${firstPaying.toLocaleString()} → ${lastPaying.toLocaleString()}.`
                    });
                  } else if (growth < 0) {
                    insights.push({
                      type: 'warning',
                      title: '⚠️ Xu hướng giảm',
                      message: `Paying Members giảm ${Math.abs(growth).toLocaleString()} thành viên (${Math.abs(Number(growthPercent))}%) trong ${weeks.length} tuần. Cần chú ý!`
                    });
                  } else {
                    insights.push({
                      type: 'info',
                      title: '➡️ Ổn định',
                      message: `Paying Members duy trì ổn định ở mức ${lastPaying.toLocaleString()} thành viên.`
                    });
                  }

                  // Calculate average metrics
                  const avgNew = weeks.reduce((sum, w) => sum + getValue('New active members', w), 0) / weeks.length;
                  const avgQuit = weeks.reduce((sum, w) => sum + getValue('Quit members', w), 0) / weeks.length;
                  const avgReturning = weeks.reduce((sum, w) => sum + getValue('Returning members', w), 0) / weeks.length;
                  const netGrowth = avgNew + avgReturning - avgQuit;

                  if (netGrowth > 0) {
                    insights.push({
                      type: 'success',
                      title: '✅ Tăng trưởng ròng dương',
                      message: `Trung bình mỗi tuần: +${avgNew.toFixed(0)} new, +${avgReturning.toFixed(0)} returning, -${avgQuit.toFixed(0)} quit → Tăng ròng ${netGrowth.toFixed(0)} members/tuần.`
                    });
                  } else if (netGrowth < 0) {
                    insights.push({
                      type: 'warning',
                      title: '⚠️ Tăng trưởng ròng âm',
                      message: `Số lượng quit (${avgQuit.toFixed(0)}/tuần) cao hơn new + returning (${(avgNew + avgReturning).toFixed(0)}/tuần). Cần cải thiện retention!`
                    });
                  }

                  // Check for anomalies
                  const quitValues = weeks.map(w => getValue('Quit members', w));
                  const maxQuit = Math.max(...quitValues);
                  const avgQuitValue = quitValues.reduce((a, b) => a + b, 0) / quitValues.length;
                  if (maxQuit > avgQuitValue * 1.5) {
                    const worstWeek = weeks[quitValues.indexOf(maxQuit)];
                    insights.push({
                      type: 'warning',
                      title: '🚨 Bất thường phát hiện',
                      message: `Tuần ${worstWeek} có ${maxQuit} quit members - cao hơn trung bình ${((maxQuit / avgQuitValue - 1) * 100).toFixed(0)}%. Cần kiểm tra nguyên nhân.`
                    });
                  }

                  // Recommendations
                  if (avgReturning < avgNew * 0.2) {
                    insights.push({
                      type: 'info',
                      title: '💡 Đề xuất',
                      message: `Returning members thấp (${avgReturning.toFixed(0)}/tuần vs ${avgNew.toFixed(0)} new). Nên tăng cường chiến dịch win-back để thu hút cựu members.`
                    });
                  }

                } else {
                  // At-risk Analysis
                  const avgRenew = weeks.reduce((sum, w) => {
                    const extend = getValue('[1] RenewExtend', w);
                    const create = getValue('[2] RenewCreate', w);
                    const upsell = getValue('[5] UpsellCreate', w);
                    return sum + extend + create + upsell;
                  }, 0) / weeks.length;
                  const avgQuit = weeks.reduce((sum, w) => sum + getValue('Quit members', w), 0) / weeks.length;
                  const avgGap = weeks.reduce((sum, w) => sum + getValue('[4] Gap', w), 0) / weeks.length;
                  const avgAtRisk = weeks.reduce((sum, w) => sum + getValue('[0] At-risk', w), 0) / weeks.length;

                  // Retention Rate
                  const retentionRate = avgAtRisk > 0 ? (avgRenew / avgAtRisk) * 100 : 0;
                  const churnRate = avgAtRisk > 0 ? (avgQuit / avgAtRisk) * 100 : 0;

                  if (retentionRate >= 50) {
                    insights.push({
                      type: 'success',
                      title: '🎯 Retention tốt',
                      message: `Tỷ lệ gia hạn trung bình: ${retentionRate.toFixed(1)}%. Có ${avgRenew.toFixed(0)}/${avgAtRisk.toFixed(0)} members gia hạn mỗi tuần.`
                    });
                  } else if (retentionRate >= 30) {
                    insights.push({
                      type: 'info',
                      title: '⚡ Retention trung bình',
                      message: `Tỷ lệ gia hạn: ${retentionRate.toFixed(1)}%. Có tiềm năng cải thiện thêm ${(50 - retentionRate).toFixed(1)}% để đạt mục tiêu 50%.`
                    });
                  } else {
                    insights.push({
                      type: 'warning',
                      title: '⚠️ Retention thấp',
                      message: `Tỷ lệ gia hạn chỉ ${retentionRate.toFixed(1)}%. Chỉ ${avgRenew.toFixed(0)}/${avgAtRisk.toFixed(0)} members gia hạn. Cần hành động ngay!`
                    });
                  }

                  // Churn Rate
                  if (churnRate > 60) {
                    insights.push({
                      type: 'warning',
                      title: '🚨 Churn rate cao',
                      message: `${churnRate.toFixed(1)}% at-risk members rời đi (${avgQuit.toFixed(0)}/tuần). Tập trung vào chiến dịch retention khẩn cấp.`
                    });
                  } else if (churnRate > 40) {
                    insights.push({
                      type: 'info',
                      title: '⚠️ Churn rate cần cải thiện',
                      message: `${churnRate.toFixed(1)}% at-risk members rời đi. Nên tăng cường chăm sóc members sắp hết hạn.`
                    });
                  } else {
                    insights.push({
                      type: 'success',
                      title: '✅ Churn rate kiểm soát tốt',
                      message: `Chỉ ${churnRate.toFixed(1)}% at-risk members rời đi. Duy trì chiến lược hiện tại.`
                    });
                  }

                  // Gap Analysis
                  if (avgGap > avgAtRisk * 0.1) {
                    insights.push({
                      type: 'warning',
                      title: '⏳ Gap members cao',
                      message: `Trung bình ${avgGap.toFixed(0)} members có khoảng trống giữa các gói (${((avgGap / avgAtRisk) * 100).toFixed(1)}%). Có thể cải thiện quy trình gia hạn để giảm gap.`
                    });
                  }

                  // Trend prediction
                  const lastWeekAtRisk = getValue('[0] At-risk', weeks[weeks.length - 1]);
                  const firstWeekAtRisk = getValue('[0] At-risk', weeks[0]);
                  const atRiskTrend = lastWeekAtRisk - firstWeekAtRisk;

                  if (atRiskTrend > 0) {
                    insights.push({
                      type: 'trend',
                      title: '📊 Dự đoán xu hướng',
                      message: `At-risk members đang tăng (+${atRiskTrend}). Nếu xu hướng tiếp tục, tuần tới dự kiến có ~${(lastWeekAtRisk + atRiskTrend * 0.5).toFixed(0)} at-risk members. Chuẩn bị nguồn lực chăm sóc.`
                    });
                  } else if (atRiskTrend < 0) {
                    insights.push({
                      type: 'trend',
                      title: '📊 Dự đoán xu hướng',
                      message: `At-risk members đang giảm (${atRiskTrend}). Xu hướng tích cực - có thể do base Paying Members ổn định hoặc tăng.`
                    });
                  }

                  // Upsell opportunity
                  const avgUpsell = weeks.reduce((sum, w) => sum + getValue('[5] UpsellCreate', w), 0) / weeks.length;
                  const upsellRate = avgRenew > 0 ? (avgUpsell / avgRenew) * 100 : 0;
                  if (upsellRate < 10) {
                    insights.push({
                      type: 'info',
                      title: '💰 Cơ hội Upsell',
                      message: `Chỉ ${upsellRate.toFixed(1)}% renewals là upsell (${avgUpsell.toFixed(0)}/tuần). Có thể tăng doanh thu bằng cách promote gói premium khi members gia hạn.`
                    });
                  }
                }

                return insights;
              };

              // Render Insights
              const renderInsights = (tableType: 'paying' | 'atRisk') => {
                const insights = generateInsights(tableType);
                if (!insights || insights.length === 0) return null;

                const getInsightColor = (type: string) => {
                  switch (type) {
                    case 'success': return isDark ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200';
                    case 'warning': return isDark ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200';
                    case 'info': return isDark ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-50 border-blue-200';
                    case 'trend': return isDark ? 'bg-purple-500/10 border-purple-500/30' : 'bg-purple-50 border-purple-200';
                    default: return isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200';
                  }
                };

                const getTextColor = (type: string) => {
                  switch (type) {
                    case 'success': return 'text-green-600 dark:text-green-400';
                    case 'warning': return 'text-orange-600 dark:text-orange-400';
                    case 'info': return 'text-blue-600 dark:text-blue-400';
                    case 'trend': return 'text-purple-600 dark:text-purple-400';
                    default: return theme.text;
                  }
                };

                return (
                  <div className={`mt-4 p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Bot className="w-5 h-5 text-[#F59E0B]" />
                      <h5 className={`text-base font-semibold ${theme.text}`}>Phân tích & Nhận xét</h5>
                    </div>
                    <div className="space-y-2">
                      {insights.map((insight, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border ${getInsightColor(insight.type)}`}
                        >
                          <p className={`font-medium text-sm mb-1 ${getTextColor(insight.type)}`}>
                            {insight.title}
                          </p>
                          <p className={`text-sm ${theme.textMuted}`}>
                            {insight.message}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              };

              // Render a summary table
              const renderTable = (
                title: string,
                metrics: MetricDef[],
                formula: React.ReactNode,
                tableType: 'paying' | 'atRisk'
              ) => {
                const showChart = tableType === 'paying' ? showPayingChart : showAtRiskChart;
                const setShowChart = tableType === 'paying' ? setShowPayingChart : setShowAtRiskChart;

                // Toggle chart with localStorage
                const toggleChart = () => {
                  const newValue = !showChart;
                  setShowChart(newValue);
                  const storageKey = tableType === 'paying' ? 'showPayingChart' : 'showAtRiskChart';
                  localStorage.setItem(storageKey, JSON.stringify(newValue));
                };

                return (
                  <div className={`${theme.cardBg} rounded-2xl border ${theme.border} overflow-hidden shadow-lg`}>
                    {/* Table Header */}
                    <div className={`px-4 py-3 border-b ${theme.border} flex items-center justify-between`}>
                      <h3 className={`text-xl font-bold ${theme.text}`}>{title}</h3>
                      <button
                        onClick={toggleChart}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showChart
                          ? 'bg-[#DC7A02] text-white'
                          : 'bg-[#F59E0B] text-white hover:bg-[#DC7A02]'
                          }`}
                      >
                        <BarChart2 className="w-4 h-4" />
                        {showChart ? 'Ẩn biểu đồ' : 'Xem biểu đồ'}
                      </button>
                    </div>

                    {/* Chart (if visible) */}
                    {showChart && renderChart(metrics, tableType, () => {
                      setShowChart(false);
                      const storageKey = tableType === 'paying' ? 'showPayingChart' : 'showAtRiskChart';
                      localStorage.setItem(storageKey, JSON.stringify(false));
                    })}

                    {/* Table Content */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={theme.tableHeader}>
                            <th className={`px-4 py-3 text-left font-semibold ${theme.textMuted} whitespace-nowrap border-b ${theme.borderLight} sticky left-0 z-10 ${theme.tableHeader} uppercase text-xs tracking-wider`}>
                              Metric
                            </th>
                            {weeks.map((week, i) => {
                              const isPeriodReportLocal = versionInfo?.reportType === 'period';
                              const isMonthlyReportLocal = versionInfo?.reportType === 'monthly';
                              let headerLabel = null;
                              let dateRange = '';

                              // Check if this is a period range (format: YYYY-MM-DD|YYYY-MM-DD)
                              if (week.includes('|')) {
                                const [startDateStr, endDateStr] = week.split('|');
                                try {
                                  const startDate = new Date(startDateStr);
                                  const endDate = new Date(endDateStr);
                                  const format = (d: Date) => `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
                                  dateRange = `${format(startDate)} - ${format(endDate)}`;
                                } catch (e) {
                                  dateRange = week;
                                }
                              } else if (isMonthlyReportLocal && week.startsWith('M ')) {
                                // Monthly report logic
                                headerLabel = getMonthName(week);
                                // Remove "M " prefix from the date range
                                dateRange = week.replace(/^M\s*/i, '');
                              } else {
                                // Weekly report logic
                                headerLabel = !isPeriodReportLocal ? getWeekNumber(week) : null;
                                // Remove "W " or "W" prefix from the date range
                                dateRange = week.replace(/^W\s*/i, '');

                                // If format is YYYY-MM-DD, convert to DD/MM/YYYY - DD/MM/YYYY (Start + 6 days)
                                if (/^\d{4}-\d{2}-\d{2}$/.test(week)) {
                                  try {
                                    const startDate = new Date(week);
                                    const endDate = new Date(startDate);
                                    endDate.setDate(startDate.getDate() + 6);

                                    const format = (d: Date) => `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
                                    dateRange = `${format(startDate)} - ${format(endDate)}`;
                                  } catch (e) {
                                    // Fallback to original string if error
                                  }
                                }
                              }

                              return (
                                <th
                                  key={i}
                                  className={`px-3 py-3 text-center font-semibold ${theme.textMuted} whitespace-nowrap border-b ${theme.borderLight} text-xs tracking-wider`}
                                >
                                  <div className="flex flex-col items-center gap-0.5">
                                    {headerLabel && (
                                      <span className={`font-bold text-sm normal-case ${isMonthlyReportLocal ? 'text-orange-500' : 'text-[#F59E0B]'}`}>{headerLabel}</span>
                                    )}
                                    <span className={isPeriodReportLocal ? "text-[#F59E0B] font-bold text-sm" : "text-[10px]"}>{dateRange}</span>
                                  </div>
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${theme.borderLight}`}>
                          {metrics.map((metric, i) => {
                            const isHighlight = metric.isHighlight;
                            // Special case for Renew Members (_renew_total) which has combined detail view
                            const hasDetail = metric.hasDetail && (METRIC_NAMES[metric.key] || metric.key === '_renew_total');
                            return (
                              <tr
                                key={i}
                                className={`${isHighlight ? theme.metricRow : ''} ${theme.tableRow}`}
                              >
                                <td className={`px-4 py-3 font-medium ${theme.text} sticky left-0 z-10 ${isHighlight ? theme.metricRow : theme.tableHeader}`}>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm">{metric.label}</span>
                                    {metric.tooltip && (
                                      <Info
                                        className={`w-4 h-4 ${theme.textMuted} cursor-pointer hover:${theme.accent} transition-colors flex-shrink-0`}
                                        onClick={() => setTooltipModal(metric.tooltip!)}
                                      />
                                    )}
                                  </div>
                                </td>
                                {weeks.map((week, j) => {
                                  let value: number;
                                  if (metric.key === '_growth_rate') {
                                    value = getGrowthRate(j);
                                  } else if (metric.isCalculated) {
                                    value = getRenewTotal(week);
                                  } else {
                                    value = getValue(metric.key, week);
                                  }

                                  // Format display value
                                  const displayValue = metric.isPercentage
                                    ? `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
                                    : value.toLocaleString();

                                  // Color for growth rate
                                  const valueColor = metric.isPercentage
                                    ? (value > 0 ? 'text-green-500' : value < 0 ? 'text-red-500' : theme.textSecondary)
                                    : theme.textSecondary;

                                  return (
                                    <td
                                      key={j}
                                      className={`px-3 py-3 ${valueColor} whitespace-nowrap text-center text-sm font-medium ${hasDetail
                                        ? 'cursor-pointer hover:bg-[#F59E0B]/20 transition-colors'
                                        : ''
                                        }`}
                                      onClick={() => {
                                        if (hasDetail && week) {
                                          loadDetail(metric.key, week, tableType);
                                        }
                                      }}
                                    >
                                      {displayValue}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Formula */}
                    <div className={`px-4 py-2 border-t ${theme.borderLight} ${theme.textMuted} text-xs`}>
                      <div className="flex items-center justify-between">
                        <span>{formula}</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const tableName = title.replace(/\s+/g, '_');
                              const csvContent = [
                                ['Week', ...metrics.map(m => m.label)].join(','),
                                ...weeks.map(week => [
                                  week,
                                  ...metrics.map(m => m.isCalculated ? getRenewTotal(week) : getValue(m.key, week))
                                ].join(','))
                              ].join('\n');
                              const blob = new Blob([csvContent], { type: 'text/csv' });
                              const link = document.createElement('a');
                              link.href = URL.createObjectURL(blob);
                              link.download = `${tableName}_${versionId}.csv`;
                              link.click();
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-500 text-white hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Export CSV
                          </button>
                          {tableType === 'paying' && (
                            <button
                              onClick={runVerification}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${showVerification
                                ? 'bg-[#DC7A02] text-white'
                                : hasPayingDiscrepancies()
                                  ? 'bg-red-500 text-white hover:bg-red-600 border-2 border-red-400 shadow-lg shadow-red-500/50 animate-pulse'
                                  : 'bg-[#F59E0B] text-white hover:bg-[#DC7A02]'
                                }`}
                            >
                              <Bot className="w-4 h-4" />
                              Kiểm tra số
                              {!showVerification && hasPayingDiscrepancies() && (
                                <span className="flex items-center justify-center w-5 h-5 bg-white text-red-500 rounded-full text-xs font-bold animate-bounce">
                                  !
                                </span>
                              )}
                            </button>
                          )}
                          {tableType === 'atRisk' && (
                            <button
                              onClick={runAtRiskVerification}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${showAtRiskVerification
                                ? 'bg-[#DC7A02] text-white'
                                : hasAtRiskDiscrepancies()
                                  ? 'bg-red-500 text-white hover:bg-red-600 border-2 border-red-400 shadow-lg shadow-red-500/50 animate-pulse'
                                  : 'bg-[#F59E0B] text-white hover:bg-[#DC7A02]'
                                }`}
                            >
                              <Bot className="w-4 h-4" />
                              Kiểm tra số
                              {!showAtRiskVerification && hasAtRiskDiscrepancies() && (
                                <span className="flex items-center justify-center w-5 h-5 bg-white text-red-500 rounded-full text-xs font-bold animate-bounce">
                                  !
                                </span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Verification Results */}
                    {tableType === 'paying' && renderVerification()}
                    {tableType === 'atRisk' && renderAtRiskVerification()}

                    {/* Expanded Detail Table - show if belongs to this table */}
                    {expandedCell && expandedCell.tableType === tableType && (
                      <div
                        className={`mt-4 mx-4 mb-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-xl border ${theme.borderLight} overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300 ease-out`}
                        style={{
                          animation: 'expandIn 0.3s ease-out forwards',
                        }}
                      >
                        <style>{`
                          @keyframes expandIn {
                            from {
                              opacity: 0;
                              transform: translateY(-10px);
                            }
                            to {
                              opacity: 1;
                              transform: translateY(0);
                            }
                          }
                        `}</style>
                        <div className={`px-4 py-3 border-b ${theme.borderLight} flex items-center justify-between`}>
                          <div>
                            <h3 className={`text-base font-semibold ${theme.text}`}>
                              {expandedCell.metric}
                            </h3>
                            <p className={`text-sm ${theme.textMuted}`}>
                              {expandedCell.week}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                const filteredRows = expandedCell.data.rows.filter((row: any) => !detailStudioFilter || row['Studio Name'] === detailStudioFilter);
                                const csvContent = [
                                  expandedCell.data.headers.join(','),
                                  ...filteredRows.map((row: any) =>
                                    expandedCell.data.headers.map((header: string) => {
                                      const value = row[header];
                                      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                                        return `"${value.replace(/"/g, '""')}"`;
                                      }
                                      return value ?? '';
                                    }).join(',')
                                  )
                                ].join('\n');
                                const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                                const link = document.createElement('a');
                                link.href = URL.createObjectURL(blob);
                                const studioSuffix = detailStudioFilter ? `_${detailStudioFilter.replace(/\s+/g, '_')}` : '';
                                const fileName = `${expandedCell.metric.replace(/\s+/g, '_')}_${expandedCell.week.replace(/\s+/g, '_')}${studioSuffix}_${versionId}.csv`;
                                link.download = fileName;
                                link.click();
                              }}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-500 text-white hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Export CSV{detailStudioFilter && ` (${detailStudioFilter})`}
                            </button>
                            <button
                              onClick={() => {
                                const scrollY = window.scrollY;
                                setExpandedCell(null);
                                // Double requestAnimationFrame for smoother scroll restoration
                                requestAnimationFrame(() => {
                                  requestAnimationFrame(() => {
                                    window.scrollTo({ top: scrollY, behavior: 'instant' });
                                  });
                                });
                              }}
                              className={`p-2 rounded-lg ${theme.buttonBg} ${theme.textMuted} hover:${theme.text} transition-colors`}
                              title="Đóng"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Filter Section */}
                        {expandedCell.data.headers.includes('Studio Name') && (
                          <div className={`px-4 py-3 border-b ${theme.borderLight} flex items-center gap-4`}>
                            <label className={`text-sm font-medium ${theme.textMuted}`}>Filter:</label>
                            <select
                              value={detailStudioFilter}
                              onChange={(e) => setDetailStudioFilter(e.target.value)}
                              className={`px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/50`}
                            >
                              <option value="">Tất cả Studio</option>
                              {[...new Set(expandedCell.data.rows.map((row: any) => row['Studio Name']).filter(Boolean))].sort().map((studio: string) => (
                                <option key={studio} value={studio}>{studio}</option>
                              ))}
                            </select>
                            {detailStudioFilter && (
                              <button
                                onClick={() => setDetailStudioFilter('')}
                                className={`text-sm ${theme.textMuted} hover:${theme.text} underline`}
                              >
                                Xóa filter
                              </button>
                            )}
                          </div>
                        )}

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={theme.tableHeader}>
                                {expandedCell.data.headers.map((header: string, i: number) => (
                                  <th
                                    key={i}
                                    className={`px-4 py-4 text-left font-semibold ${theme.textMuted} whitespace-nowrap border-b ${theme.borderLight} uppercase text-xs tracking-wider`}
                                  >
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className={`divide-y ${theme.borderLight}`}>
                              {expandedCell.data.rows
                                .filter((row: any) => !detailStudioFilter || row['Studio Name'] === detailStudioFilter)
                                .map((row: any, i: number) => (
                                <tr key={i} className={theme.tableRow}>
                                  {expandedCell.data.headers.map((header: string, j: number) => {
                                    const value = row[header];
                                    const isUrl = typeof value === 'string' && value.startsWith('http');
                                    return (
                                      <td key={j} className={`px-4 py-3 ${theme.textSecondary} whitespace-nowrap`}>
                                        {isUrl ? (
                                          <a
                                            href={value}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#F59E0B] hover:underline"
                                          >
                                            {value}
                                          </a>
                                        ) : (
                                          value ?? ''
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {expandedCell.data.rows.filter((row: any) => !detailStudioFilter || row['Studio Name'] === detailStudioFilter).length === 0 && (
                            <div className={`text-center py-16 ${theme.textMuted}`}>Không có dữ liệu</div>
                          )}
                          <div className={`px-4 py-3 border-t ${theme.borderLight} ${theme.textMuted} text-sm`}>
                            Tổng: {expandedCell.data.rows.filter((row: any) => !detailStudioFilter || row['Studio Name'] === detailStudioFilter).length} / {expandedCell.data.rows.length} records
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              };

              return (
                <div className="space-y-6">
                  {/* Table 1: Paying Members */}
                  {renderTable(
                    'Paying Members',
                    payingMetrics.filter(m => m.key !== '_growth_rate' || showGrowthRate),
                    <span className="flex flex-col gap-2 w-full">
                      <span>
                        <strong>Công thức:</strong> Paying<sub>N</sub> = Paying<sub>N-1</sub> - Quit<sub>N</sub> - TemporaryLeft<sub>N</sub> + New<sub>N</sub> + Returning<sub>N</sub>
                      </span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showGrowthRate}
                          onChange={(e) => setShowGrowthRate(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-[#F59E0B] focus:ring-[#F59E0B]"
                        />
                        <span className="text-sm">Growth Rate</span>
                      </label>
                    </span>,
                    'paying'
                  )}

                  {/* Table 2: At-risk Members */}
                  {renderTable(
                    'At-risk Members',
                    atRiskMetrics,
                    <span>
                      <strong>Công thức:</strong> At-risk = Renew + Quit + Gap
                      <span className={`ml-4 ${theme.textMuted}`}>
                        (Renew = RenewExtend + RenewCreate + UpsellCreate)
                      </span>
                    </span>,
                    'atRisk'
                  )}
                </div>
              );
            })()
          ) : null}
        </div>

        {/* Footer Info */}
        {summaryData && (
          <div className={`mt-6 px-6 py-4 ${theme.cardBg} rounded-xl border ${theme.border} ${theme.textMuted} text-sm`}>
            <p className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full bg-[#F59E0B]`}></span>
              Click vào số liệu để xem chi tiết danh sách members
            </p>
          </div>
        )}
      </main>
      {/* Tooltip Modal */}
      {tooltipModal && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setTooltipModal(null)}
        >
          <div
            className={`max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-2xl ${isDark ? 'bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] shadow-2xl shadow-black/60' : 'bg-white/80 backdrop-blur-xl border border-gray-200 shadow-2xl'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`sticky top-0 px-6 py-4 border-b ${isDark ? 'border-white/[0.05] bg-white/[0.02] backdrop-blur-md' : 'border-gray-200 bg-gray-50/80 backdrop-blur-md'} flex items-start justify-between`}>
              <h3 className={`text-2xl font-bold ${isDark ? 'text-[#FBBF24]' : 'text-[#F59E0B]'} tracking-tight`}>
                {tooltipModal.title}
              </h3>
              <button
                onClick={() => setTooltipModal(null)}
                className={`p-2 rounded-lg ${theme.buttonBg} ${theme.textMuted} hover:${theme.text} transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B]`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6 space-y-4">
              <div>
                <p className={`text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {tooltipModal.description}
                </p>
              </div>

              {tooltipModal.formula && (
                <div className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <p className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Công thức:
                  </p>
                  <code className={`block text-sm p-4 rounded-lg font-mono ${isDark ? 'bg-gray-800/70 text-cyan-300 border border-white/10' : 'bg-gray-50 text-cyan-700 border border-gray-200'}`}>
                    {tooltipModal.formula}
                  </code>
                </div>
              )}

              {tooltipModal.note && (
                <div className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <p className={`text-sm leading-relaxed whitespace-pre-line ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {tooltipModal.note}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

